/**
 * Component Renderer Core Implementation
 *
 * Main orchestrator that handles form completion events, polls the credit decision API,
 * and renders the appropriate Webflow components based on scenario results.
 */

import type { FormCompletionData } from '../integration'
import type { PollingConfig, PollingResult, CreditDecisionResponse, DataVariables } from '../api'
import type { ComponentSystemConfig, EnvironmentConfig } from '../types'

import { pollCreditDecision } from '../api'
import {
  COMPONENT_CONFIG,
  getComponentActions,
  getEnvironmentConfig,
  detectBrand,
  getProcessingComponentId,
  getOutcomeComponentIds
} from '../config'
import { getGclid, getGcid, cleanSessionId } from '../../utils/tracking-helpers'
import html2canvas from 'html2canvas'
import { logger, LogCategory, trackCreditDecision, trackOutcomeButtonClick } from '../../logging'
import { MultiStepController } from '../../multi-step/multi-step-controller'

// Note: Window.dataLayer is declared in src/logging/analytics-logger.ts

/**
 * Component Renderer Configuration
 */
export interface ComponentRendererOptions {
  /** Environment to use (development/production) */
  environment: 'development' | 'production'
  /** Debug mode override */
  debugMode?: boolean
  /** Custom API headers */
  apiHeaders?: Record<string, string>
  /** Custom polling configuration overrides */
  pollingOverrides?: Partial<PollingConfig>
}

/**
 * Component Rendering Result
 */
export interface ComponentRenderingResult {
  /** Whether rendering was successful */
  success: boolean
  /** Scenario code that was processed */
  scenarioCode?: number
  /** Components that were shown */
  componentsShown: string[]
  /** Components that were hidden */
  componentsHidden: string[]
  /** Whether fallback scenario was used */
  usedFallback: boolean
  /** Total processing time in milliseconds */
  processingTime: number
  /** Error message if unsuccessful */
  error?: string
  /** Credit decision API response */
  creditDecision?: CreditDecisionResponse
  /** API polling result */
  pollingResult?: PollingResult
}

/**
 * Completion State (persisted in localStorage)
 * Stores form completion state for 24 hours to prevent duplicate submissions
 * Includes full credit decision response for instant restoration on refresh
 */
export interface CompletionState {
  /** Session ID from successful form completion */
  sessionId: string
  /** Scenario code that was rendered */
  scenario: number
  /** Timestamp when state was saved */
  timestamp: number
  /** Expiration timestamp (24 hours from save) */
  expiresAt: number
  /** Flag indicating form was completed */
  hasCompletedForm: boolean
  /** Full credit decision response (for instant restoration) */
  creditDecision?: CreditDecisionResponse
  /** Extracted data variables (for instant restoration) */
  dataVariables?: DataVariables
  /** Component actions that were performed */
  componentActions?: {
    show: string[]
    hide: string[]
    useFallback: boolean
  }
  /** Form data for offer link placeholder replacement */
  formData?: FormCompletionData['formData']
}

/**
 * localStorage key for completion state persistence
 */
const COMPLETION_STATE_KEY = 'accs_completion_state'

/**
 * localStorage key for credit locked retry count (scenarios 97/98)
 */
const CREDIT_LOCKED_RETRY_COUNT_KEY = 'credit_locked_retry_count'

/**
 * Maximum number of retries allowed for credit locked scenarios
 */
const MAX_CREDIT_LOCKED_RETRIES = 1

/**
 * Countdown duration in seconds for credit locked retry
 */
const CREDIT_LOCKED_COUNTDOWN_SECONDS = 60

/**
 * Main Component Renderer Class
 *
 * Handles the complete workflow from form completion to component rendering:
 * 1. Receive form completion event
 * 2. Poll credit decision API
 * 3. Map scenario to component actions
 * 4. Render appropriate components
 */
export class ComponentRenderer {
  private options: ComponentRendererOptions
  private envConfig: EnvironmentConfig
  private debugMode: boolean
  private brand: 'accs' | 'creditdirect'
  private refreshPreventionEnabled: boolean = false
  private lastFormData: FormCompletionData['formData'] | undefined
  private scrollLocked: boolean = false
  private savedScrollPosition: number = 0
  private loadingMessageInterval: ReturnType<typeof setInterval> | null = null
  private loadingPatienceTimeout: ReturnType<typeof setTimeout> | null = null

  constructor(options: ComponentRendererOptions) {
    this.options = options

    // Get environment configuration
    const envConfig = getEnvironmentConfig(options.environment)
    if (!envConfig) {
      throw new Error(`Invalid environment: ${options.environment}`)
    }
    this.envConfig = envConfig

    // Detect brand from __SITE_CONFIG__ first (set by entry point), then fall back to hostname detection
    const siteConfig = typeof window !== 'undefined' && (window as any).__SITE_CONFIG__
    const configBrand = siteConfig?.brand
    const detectedBrand = detectBrand()
    this.brand = configBrand || detectedBrand

    // Set debug mode
    this.debugMode = options.debugMode !== undefined ? options.debugMode : envConfig.debugMode

    console.log('🎯 ComponentRenderer Brand Detection', {
      __SITE_CONFIG__: siteConfig,
      configBrand,
      detectedBrand,
      finalBrand: this.brand,
      hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A'
    })

    if (this.debugMode) {
      console.log('🎯 ComponentRenderer initialized', {
        environment: options.environment,
        brand: this.brand,
        debugMode: this.debugMode,
        apiBaseUrl: envConfig.apiBaseUrl
      })
    }

    // Debug: Confirm screenshot capture methods are loaded
    console.log('📸 [INIT] Screenshot capture registered:', {
      captureMethod: typeof this.captureOutcomeScreenshot === 'function',
      uploadMethod: typeof this.uploadScreenshotViaEdgeFunction === 'function',
      findVisibleMethod: typeof this.findVisibleOutcomeComponent === 'function',
      html2canvasLoaded: typeof window !== 'undefined' && typeof (window as any).html2canvas !== 'undefined'
    })
  }

  /**
   * Handle form completion event - main entry point
   */
  async handleFormCompletion(data: FormCompletionData): Promise<ComponentRenderingResult> {
    const startTime = Date.now()

    // Log form completion event
    logger.info(LogCategory.COMPONENT, 'Form completion received', {
      session_id: data.sessionId,
      is_form_submission: data.isFormSubmission,
      brand: this.brand
    })

    if (this.debugMode) {
      console.log('🎯 Processing form completion:', data)
    }

    // Store form data for use in link placeholder replacement
    this.lastFormData = data.formData

    // Fire GTM conversion event immediately on form submission with enhanced conversions
    await this.fireGTMConversionEvent(data.sessionId, 0, data.formData)

    try {
      // Step 0: Show processing component immediately
      await this.showProcessingComponent()
      this.startLoadingMessages()

      // Step 1: Check for scenario override from query parameter
      const overrideScenario = this.getScenarioOverride()
      if (overrideScenario !== null) {
        console.log(`🔧 Scenario override detected: ${overrideScenario}`)
        this.stopLoadingMessages()
        return this.renderOverrideScenario(overrideScenario, startTime, data)
      }

      // Step 2: Call credit decision API
      const pollingResult = await this.pollForCreditDecision(data)
      this.stopLoadingMessages()

      if (!pollingResult.success || !pollingResult.response) {
        // No credit decision available - post error to backend and use fallback
        await this.reportCreditDecisionError(data.sessionId, pollingResult)
        return this.renderFallbackComponents(startTime, pollingResult, undefined, data.sessionId)
      }

      // Step 3: Map scenario to component actions
      const scenarioCode = pollingResult.response.scenario
      const componentActions = getComponentActions(scenarioCode, this.brand)

      // Log scenario mapping
      logger.info(LogCategory.COMPONENT, 'Scenario mapped', {
        session_id: data.sessionId,
        scenario_code: scenarioCode,
        brand: this.brand,
        components_to_show: componentActions.show,
        components_to_hide: componentActions.hide,
        use_fallback: componentActions.useFallback
      })

      // Track credit decision in GTM
      const outcomeType = scenarioCode === 2 || scenarioCode === 4 ? 'approved' :
                          scenarioCode >= 5 ? 'declined' : 'other'
      trackCreditDecision(data.sessionId, this.brand, scenarioCode, outcomeType)

      if (this.debugMode) {
        console.log('🎯 Scenario mapping:', {
          scenarioCode,
          brand: this.brand,
          actions: componentActions
        })
      }

      // Fire prime_qualified event for scenarios 2 and 4
      if (scenarioCode === 2 || scenarioCode === 4) {
        await this.firePrimeQualifiedEvent(data.sessionId, scenarioCode, data.formData)
      }

      // Fire scenario-specific conversion event (O1, O2, O4, O5, O7, O8, O10)
      await this.fireScenarioConversionEvent(data.sessionId, scenarioCode, data.formData)

      // DISABLED 2026-06-11 (per Shawn): scenarios 5 and 7 must ALWAYS render the
      // additional-info form, regardless of any credit-pull-stage PartPost2 offer.
      // The credit pull began returning metadata.externalResponse.partPost2Offer
      // .redirectUrl on 5/7, which this branch honored — redirecting away and
      // skipping the form. The real accept/redirect now happens at the
      // additional-info SUBMIT (form-controller -> performAcceptRedirect), not at
      // the credit pull. Re-enable only if instructed to restore 5/7 redirects.
      //
      // if (scenarioCode === 5 || scenarioCode === 7) {
      //   const extResponse = pollingResult.response.metadata?.externalResponse
      //   const partPost2Redirect = extResponse?.partPost2Offer?.redirectUrl
      //   if (partPost2Redirect) {
      //     console.log('🔀 PartPost2 redirect:', partPost2Redirect)
      //     window.location.href = partPost2Redirect
      //     return {
      //       success: true,
      //       scenarioCode,
      //       componentsShown: [],
      //       componentsHidden: [],
      //       usedFallback: false,
      //       processingTime: Date.now() - startTime,
      //       creditDecision: pollingResult.response,
      //       pollingResult
      //     }
      //   }
      // }

      // Send lead attributes to session recording (LogRocket)
      this.sendSessionRecordingAttributes(data.sessionId, scenarioCode, data.formData, pollingResult.response)

      // Step 3: Render components
      const renderingSuccess = await this.renderComponents(
        componentActions.show,
        componentActions.hide,
        data.sessionId,
        scenarioCode
      )

      // Step 4: Inject data variables from API response or DebtSettlementEstimate
      const dataVariables = this.extractDataVariables(pollingResult.response)
      if (dataVariables) {
        this.injectDataVariables(dataVariables)
      }

      // Step 4.5: Render multiple loan offers if available
      this.renderMultipleOffers(pollingResult.response)

      // Step 5: Save completion state to localStorage (24 hour persistence)
      // Include full response and data for instant restoration on refresh
      if (renderingSuccess && data.isFormSubmission) {
        this.saveCompletionState(
          data.sessionId,
          scenarioCode,
          pollingResult.response,
          dataVariables,
          componentActions
        )
      }

      // Step 6: Enable refresh prevention after scenario is rendered
      if (renderingSuccess) {
        this.enableRefreshPrevention()
      }

      // Step 7: Capture screenshot of the fully rendered and populated outcome
      if (renderingSuccess) {
        this.triggerScreenshot(data.sessionId, scenarioCode, componentActions.show)
      }

      return {
        success: renderingSuccess,
        scenarioCode,
        componentsShown: componentActions.show,
        componentsHidden: componentActions.hide,
        usedFallback: componentActions.useFallback,
        processingTime: Date.now() - startTime,
        creditDecision: pollingResult.response,
        pollingResult
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown rendering error'

      if (this.debugMode) {
        console.error('🎯 Component rendering failed:', error)
      }

      // On error, render fallback components
      return this.renderFallbackComponents(startTime, undefined, errorMessage, data.sessionId)
    }
  }

  /**
   * Trigger screenshot capture (fire-and-forget)
   * Designed to be called AFTER all rendering and data injection is complete
   */
  private triggerScreenshot(sessionId: string, scenarioCode: number, componentsShown: string[]): void {
    // Scenarios 5 & 7 render the INTERACTIVE additional-information form, not a
    // final display-only outcome. Capturing it is pointless (the form is empty
    // at capture time) and actively harmful: captureOutcomeScreenshot() force-
    // scrolls the page (scrollIntoView + scrollBy) ~1.5-3s after render and
    // runs html2canvas (clones the whole document, heavy main-thread work) —
    // exactly when the user starts filling fields. Users were getting their
    // first click (e.g. the Driver's License State select) interrupted. Skip it.
    if (scenarioCode === 5 || scenarioCode === 7) {
      if (this.debugMode) {
        console.log('📸 [SCREENSHOT] Skipped for interactive additional-info form (scenario', scenarioCode, ')')
      }
      return
    }

    // Prefer explicit shown component, otherwise find any visible outcome component
    const outcomeComponentId = componentsShown.length > 0
      ? componentsShown[0]
      : this.findVisibleOutcomeComponent()

    if (outcomeComponentId) {
      // Fire and forget - don't await to avoid blocking
      this.captureOutcomeScreenshot(outcomeComponentId, sessionId, scenarioCode)
        .then(url => {
          if (this.debugMode && url) {
            console.log('✅ [SCREENSHOT] Screenshot captured successfully')
          }
        })
        .catch(error => {
          if (this.debugMode) {
            console.error('❌ [SCREENSHOT] Capture failed:', error)
          }
        })
    } else if (this.debugMode) {
      console.warn('⚠️ [SCREENSHOT] Skipped: No visible outcome component found')
    }
  }

  /**
   * Swap the additional-info form (O5/O7) for the decline outcome.
   *
   * After the additional-info submit, dcapi returns accept (redirect, handled
   * in the form controller) or decline (scenario 7). On decline the form
   * controller fires `additional-info:non-accept-response`; the lead-tracker
   * listens and calls this to hide the form and show the unqualified outcome —
   * the same page scenario 5/7 showed before the additional-info flow existed.
   */
  public async showAdditionalInfoDecline(sessionId: string): Promise<void> {
    await this.renderComponents(
      ['CdOutcomeUnqualified'],
      ['CdOutcomeStopGoAdditionalInformation'],
      sessionId,
      7
    )
    // The decline page is a display-only outcome — screenshot it like any other.
    this.triggerScreenshot(sessionId, 0, ['CdOutcomeUnqualified'])
  }

  /**
   * Get scenario override from URL query parameter
   * Supports: ?scenario=1 through ?scenario=10, and ?scenario=90 through ?scenario=99
   */
  private getScenarioOverride(): number | null {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const scenarioParam = urlParams.get('scenario')

      if (!scenarioParam) {
        return null
      }

      const scenarioNum = parseInt(scenarioParam, 10)

      // Validate scenario code (1-10, 90-99)
      if (isNaN(scenarioNum)) {
        console.warn(`⚠️ Invalid scenario override: "${scenarioParam}" is not a number`)
        return null
      }

      if (!this.isValidScenario(scenarioNum)) {
        console.warn(`⚠️ Invalid scenario override: ${scenarioNum} is not in valid range (1-10, 90-99)`)
        return null
      }

      return scenarioNum

    } catch (error) {
      console.error('❌ Failed to parse scenario override:', error)
      return null
    }
  }

  /**
   * Get data variable overrides from URL query parameters
   * Supports: ?dr_program_quote=5000&prequal_amt=10000 etc.
   */
  private getDataVariableOverrides(): DataVariables | null {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const dataVars: DataVariables = {}

      const validKeys: (keyof DataVariables)[] = [
        'dr_program_quote',
        'dr_prequal_debt',
        'prequal_amt',
        'prequal_payment',
        'dr_est_program_length',
        'total_monthly_savings',
        'offer_logo',
        'offer_lender',
        'offer_prequal_amount',
        'offer_prequal_payment',
        'lender_apr',
        'offer_url'
      ]

      let hasAnyVars = false

      for (const key of validKeys) {
        const value = urlParams.get(key)
        if (value !== null) {
          // Format number fields with commas if they're numbers
          if (key === 'dr_prequal_debt' || key === 'offer_prequal_amount') {
            const numValue = parseFloat(value)
            if (!isNaN(numValue)) {
              (dataVars as any)[key] = this.formatNumberWithCommas(numValue)
            } else {
              (dataVars as any)[key] = value
            }
          } else {
            (dataVars as any)[key] = value
          }
          hasAnyVars = true
        }
      }

      if (!hasAnyVars) {
        return null
      }

      if (this.debugMode) {
        console.log('🔧 Data variable overrides detected:', dataVars)
      }

      return dataVars

    } catch (error) {
      console.error('❌ Failed to parse data variable overrides:', error)
      return null
    }
  }

  /**
   * Validate scenario codes
   */
  private isValidScenario(scenario: number): boolean {
    return (scenario >= 1 && scenario <= 10) || (scenario >= 90 && scenario <= 99)
  }

  /**
   * Render components based on override scenario (skip API call)
   */
  private async renderOverrideScenario(
    scenarioCode: number,
    startTime: number,
    data: FormCompletionData
  ): Promise<ComponentRenderingResult> {
    console.log(`🔧 Using scenario override: ${scenarioCode}`)

    // Get data variable overrides from query parameters
    const dataVariables = this.getDataVariableOverrides()

    // Create mock credit decision response
    const mockResponse: CreditDecisionResponse = {
      sessionId: data.sessionId,
      scenario: scenarioCode,
      timestamp: new Date().toISOString(),
      decision: `Override scenario ${scenarioCode}`,
      dataVariables: dataVariables || undefined,
      metadata: {
        externalResponse: { override: true }
      }
    }

    // Map scenario to component actions
    const componentActions = getComponentActions(scenarioCode, this.brand)

    if (this.debugMode) {
      console.log('🎯 Override scenario mapping:', {
        scenarioCode,
        brand: this.brand,
        actions: componentActions,
        dataVariables
      })
    }

    // Render components
    const renderingSuccess = await this.renderComponents(
      componentActions.show,
      componentActions.hide,
      data.sessionId,
      scenarioCode
    )

    // Inject data variables into components
    if (dataVariables) {
      this.injectDataVariables(dataVariables)
    }

    // Render multiple loan offers if available (override scenario)
    this.renderMultipleOffers(mockResponse)

    // Fire scenario-specific conversion event for override testing
    await this.fireScenarioConversionEvent(data.sessionId, scenarioCode, data.formData)

    // Send lead attributes to session recording (LogRocket)
    this.sendSessionRecordingAttributes(data.sessionId, scenarioCode, data.formData, mockResponse)

    // Capture screenshot for override scenario
    if (renderingSuccess) {
      this.triggerScreenshot(data.sessionId, scenarioCode, componentActions.show)
    }

    return {
      success: renderingSuccess,
      scenarioCode,
      componentsShown: componentActions.show,
      componentsHidden: componentActions.hide,
      usedFallback: componentActions.useFallback,
      processingTime: Date.now() - startTime,
      creditDecision: mockResponse
    }
  }

  /**
   * Call credit decision API
   */
  private async pollForCreditDecision(data: FormCompletionData): Promise<PollingResult> {
    const pollingConfig: PollingConfig = {
      apiBaseUrl: this.envConfig.apiBaseUrl,
      sessionId: data.sessionId,
      intervalMs: this.envConfig.polling.intervalMs,
      timeoutMs: this.envConfig.polling.timeoutMs,
      maxAttempts: this.envConfig.polling.maxAttempts,
      debugMode: this.debugMode,
      headers: this.options.apiHeaders,
      // Include tracking data for Google Ads
      trackingData: {
        gclid: getGclid(),
        gcid: getGcid(),
        external_transaction_id: cleanSessionId(data.sessionId),
        client_ip_address: undefined // Will be extracted by backend if needed
      },
      ...this.options.pollingOverrides
    }

    const pollingCallbacks = {
      onStart: this.debugMode ? (state: any) => {
        console.log('📞 Credit decision API call started:', state)
      } : undefined,

      onAttempt: this.debugMode ? (state: any, attempt: number) => {
        console.log(`📞 Credit decision API call (attempt ${attempt})`)
      } : undefined,

      onDecision: this.debugMode ? (response: CreditDecisionResponse) => {
        console.log('✅ Credit decision received:', response)
      } : undefined,

      onTimeout: this.debugMode ? (state: any) => {
        console.log('⏰ Credit decision API timed out:', state)
      } : undefined,

      onError: this.debugMode ? (error: string) => {
        console.warn('⚠️ Credit decision API error:', error)
      } : undefined
    }

    return await pollCreditDecision(pollingConfig, pollingCallbacks)
  }

  /**
   * Report credit decision error to backend
   */
  private async reportCreditDecisionError(sessionId: string, pollingResult: PollingResult): Promise<void> {
    try {
      const errorMessage = pollingResult.error || (pollingResult.state.status === 'timeout' ? 'Credit decision API timeout after 120 seconds' : 'Unknown error')

      if (this.debugMode) {
        console.log('📤 Reporting credit decision error to backend:', errorMessage)
      }

      // Get Supabase config from unified config
      const config = (window as any).__LEAD_TRACKER_CONFIG__
      if (!config?.supabase) {
        console.warn('⚠️ Cannot report error: Supabase config not available')
        return
      }

      // Update lead with error status and message
      const response = await fetch(`${config.supabase.url}/rest/v1/simple_leads?session_id=eq.${sessionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'apikey': config.supabase.key,
          'Authorization': `Bearer ${config.supabase.key}`,
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify({
          status: 'error',
          error_message: errorMessage,
          updated_at: new Date().toISOString()
        })
      })

      if (response.ok) {
        if (this.debugMode) {
          console.log('✅ Error reported to backend successfully')
        }
      } else {
        console.warn('⚠️ Failed to report error to backend:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('❌ Error reporting credit decision failure:', error)
      // Don't throw - we still want to render fallback components
    }
  }

  /**
   * Render fallback components when credit decision is unavailable
   */
  private async renderFallbackComponents(
    startTime: number,
    pollingResult?: PollingResult,
    error?: string,
    sessionId?: string
  ): Promise<ComponentRenderingResult> {
    if (this.debugMode) {
      console.log('🎯 Rendering fallback components due to:', error || 'no credit decision')
    }

    const fallbackActions = getComponentActions(-1, this.brand) // Invalid scenario triggers fallback

    const renderingSuccess = await this.renderComponents(
      fallbackActions.show,
      fallbackActions.hide,
      sessionId,
      0  // Use scenario 0 for error/fallback screenshots
    )

    return {
      success: renderingSuccess,
      componentsShown: fallbackActions.show,
      componentsHidden: fallbackActions.hide,
      usedFallback: true,
      processingTime: Date.now() - startTime,
      error,
      pollingResult
    }
  }

  /**
   * Show processing component immediately after form submission
   */
  /**
   * Inject rotating messages + indeterminate progress bar into the processing
   * component while the credit decision API call is in flight.
   */
  private startLoadingMessages(): void {
    const MESSAGES = [
      'Reviewing your application\u2026',
      'Checking your credit profile\u2026',
      'Exploring loan options for you\u2026',
      'Almost there\u2026',
      'Finalizing your results\u2026',
    ]
    const PATIENCE_MSG = 'This is taking a little longer than usual \u2014 hang tight, we\u2019re still working on it.'
    const SWAP_INTERVAL_MS = 2800
    const PATIENCE_THRESHOLD_MS = 15000
    const FADE_MS = 350

    const processingSelector = this.brand === 'creditdirect'
      ? '[data-component="cd-form-processing"]'
      : '[data-component="accs-form-processing"]'
    const container = document.querySelector(processingSelector) as HTMLElement | null
    if (!container) return

    // Inject CSS once
    const STYLE_ID = '__cd_loading_style__'
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style')
      style.id = STYLE_ID
      style.textContent = `
        #__cd_loading_messages__ {
          text-align: center;
          padding: 20px 16px 8px;
        }
        #__cd_loading_msg_text__ {
          font-size: 15px;
          color: #666;
          min-height: 1.4em;
          transition: opacity ${FADE_MS}ms ease;
        }
        #__cd_progress_track__ {
          width: 100%;
          max-width: 220px;
          margin: 14px auto 0;
          height: 3px;
          background: rgba(0,0,0,0.08);
          border-radius: 2px;
          overflow: hidden;
        }
        #__cd_progress_bar__ {
          height: 100%;
          width: 38%;
          background: #3182ce;
          border-radius: 2px;
          animation: __cd_sweep__ 1.7s ease-in-out infinite;
        }
        @keyframes __cd_sweep__ {
          0%   { transform: translateX(-160%); }
          100% { transform: translateX(420%); }
        }
      `
      document.head.appendChild(style)
    }

    // Inject message container
    const wrapper = document.createElement('div')
    wrapper.id = '__cd_loading_messages__'
    wrapper.innerHTML = `
      <div id="__cd_loading_msg_text__">${MESSAGES[0]}</div>
      <div id="__cd_progress_track__"><div id="__cd_progress_bar__"></div></div>
    `
    container.appendChild(wrapper)

    let msgIndex = 0
    const msgEl = wrapper.querySelector('#__cd_loading_msg_text__') as HTMLElement

    const swapMessage = (text: string) => {
      msgEl.style.opacity = '0'
      setTimeout(() => {
        msgEl.textContent = text
        msgEl.style.opacity = '1'
      }, FADE_MS)
    }

    this.loadingMessageInterval = setInterval(() => {
      msgIndex = (msgIndex + 1) % MESSAGES.length
      swapMessage(MESSAGES[msgIndex])
    }, SWAP_INTERVAL_MS)

    this.loadingPatienceTimeout = setTimeout(() => {
      swapMessage(PATIENCE_MSG)
      if (this.loadingMessageInterval !== null) {
        clearInterval(this.loadingMessageInterval)
        this.loadingMessageInterval = null
      }
    }, PATIENCE_THRESHOLD_MS)
  }

  /**
   * Stop rotating messages and remove the injected UI.
   */
  private stopLoadingMessages(): void {
    if (this.loadingMessageInterval !== null) {
      clearInterval(this.loadingMessageInterval)
      this.loadingMessageInterval = null
    }
    if (this.loadingPatienceTimeout !== null) {
      clearTimeout(this.loadingPatienceTimeout)
      this.loadingPatienceTimeout = null
    }
    const wrapper = document.getElementById('__cd_loading_messages__')
    if (wrapper) {
      wrapper.style.transition = 'opacity 300ms ease'
      wrapper.style.opacity = '0'
      setTimeout(() => wrapper.remove(), 310)
    }
  }

  private async showProcessingComponent(): Promise<void> {
    try {
      console.log('⏳ Attempting to show processing component')

      // Get brand-specific component IDs
      const processingComponentId = getProcessingComponentId(this.brand)
      const processingSelector = this.brand === 'creditdirect'
        ? '[data-component="cd-form-processing"]'
        : '[data-component="accs-form-processing"]'

      // Check if processing component exists in DOM
      const processingElements = document.querySelectorAll(processingSelector)
      console.log(`🔍 Found ${processingElements.length} processing component element(s) in DOM`)

      if (processingElements.length === 0) {
        console.error(`❌ Processing component not found in DOM! Check that ${processingSelector} exists in Webflow`)
        return
      }

      // Hide all outcome components for the current brand
      const allOutcomeComponents = getOutcomeComponentIds(this.brand)

      for (const componentId of allOutcomeComponents) {
        await this.hideComponent(componentId)
      }

      // Lock scroll before showing processing component
      this.lockScroll()

      // Show processing component
      await this.showComponent(processingComponentId)

      // Verify it's visible
      const processingEl = processingElements[0] as HTMLElement
      const parentSection = processingEl.closest('.section') as HTMLElement
      console.log('✅ Processing component display:', {
        element: processingEl.style.display,
        parentSection: parentSection?.style.display,
        elementClasses: processingEl.className,
        parentClasses: parentSection?.className
      })

    } catch (error) {
      console.error('❌ Failed to show processing component:', error)
    }
  }

  /**
   * Render components using show/hide strategy
   */
  private async renderComponents(
    show: string[],
    hide: string[],
    sessionId?: string,
    scenarioCode?: number
  ): Promise<boolean> {
    try {
      console.log('🎯 [RENDER] Starting renderComponents...', { show, hide, sessionId, scenarioCode })

      // Hide all components first
      console.log('🔍 [RENDER] Hiding components:', hide)
      for (const componentId of hide) {
        console.log(`  ↳ Hiding: ${componentId}`)
        await this.hideComponent(componentId)
      }
      console.log('✅ [RENDER] All components hidden')

      // Then show the required components
      console.log('🔍 [RENDER] Showing components:', show)
      for (const componentId of show) {
        console.log(`  ↳ Showing: ${componentId}`)
        await this.showComponent(componentId)
      }
      console.log('✅ [RENDER] All components shown')

      // Unlock scroll after rendering final outcome
      this.unlockScroll()

      // Fix retry button links to preserve query parameters and configure scenario-specific behavior
      this.fixRetryButtonLinks(show, scenarioCode)

      // Replace 'sid_here' placeholders in links with actual session ID
      this.replaceSessionIdPlaceholders(show, sessionId)

      // Replace placeholders in offer links (e.g. 5k-funds URLs) with form data
      this.replaceOfferLinkPlaceholders(show, sessionId)

      // Setup click tracking for buttons in outcome components
      this.setupOutcomeButtonTracking(show, sessionId, scenarioCode)

      console.log('✅ [RENDER] Component rendering completed successfully')

      return true

    } catch (error) {
      console.error('❌ [RENDER] Component rendering failed:', error)
      console.error('❌ [RENDER] Stack trace:', error)
      // Make sure to unlock scroll even on error
      this.unlockScroll()
      return false
    }
  }

  /**
   * Show a component using CSS display properties
   */
  private async showComponent(componentId: string): Promise<void> {
    const component = COMPONENT_CONFIG.components[componentId]
    if (!component) {
      throw new Error(`Component not found: ${componentId}`)
    }

    const elements = document.querySelectorAll(component.selector)

    if (elements.length === 0) {
      if (this.debugMode) {
        console.warn(`⚠️ No elements found for component: ${componentId} (${component.selector})`)
      }
      logger.warn(LogCategory.COMPONENT, 'Component not found in DOM', {
        component_id: componentId,
        selector: component.selector,
        action: 'show'
      })
      return
    }

    elements.forEach((element: Element) => {
      const htmlElement = element as HTMLElement

      // Set display property
      htmlElement.style.display = component.visibleDisplay

      // Add show classes
      component.showClasses?.forEach(cls => htmlElement.classList.add(cls))

      // Remove hide classes
      component.hideClasses?.forEach(cls => htmlElement.classList.remove(cls))

      // CRITICAL: Also show parent section if it's hidden
      const parentSection = htmlElement.closest('.section')
      if (parentSection) {
        const parentEl = parentSection as HTMLElement
        parentEl.style.display = 'block'
        parentEl.classList.remove('component-hidden')
      }
    })

    // Log component shown
    logger.debug(LogCategory.COMPONENT, `Component shown: ${componentId}`, {
      component_id: componentId,
      selector: component.selector,
      elements_found: elements.length
    })

    if (this.debugMode) {
      console.log(`👁️ Showed component: ${componentId} (${elements.length} elements)`)
    }
  }

  /**
   * Hide a component using CSS display properties
   */
  private async hideComponent(componentId: string): Promise<void> {
    const component = COMPONENT_CONFIG.components[componentId]
    if (!component) {
      throw new Error(`Component not found: ${componentId}`)
    }

    const elements = document.querySelectorAll(component.selector)

    if (elements.length === 0) {
      // Not finding elements to hide is not necessarily an error
      if (this.debugMode) {
        console.log(`ℹ️ No elements found to hide for component: ${componentId}`)
      }
      return
    }

    elements.forEach((element: Element) => {
      const htmlElement = element as HTMLElement

      // Set display property
      htmlElement.style.display = component.hiddenDisplay

      // Add hide classes
      component.hideClasses?.forEach(cls => htmlElement.classList.add(cls))

      // Remove show classes
      component.showClasses?.forEach(cls => htmlElement.classList.remove(cls))
    })

    // Log component hidden
    logger.debug(LogCategory.COMPONENT, `Component hidden: ${componentId}`, {
      component_id: componentId,
      selector: component.selector,
      elements_found: elements.length
    })

    if (this.debugMode) {
      console.log(`🙈 Hid component: ${componentId} (${elements.length} elements)`)
    }
  }

  /**
   * Format number with comma separators (no decimals)
   * Example: 25000 -> "25,000"
   */
  private formatNumberWithCommas(value: number): string {
    return Math.round(value).toLocaleString('en-US', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    })
  }

  /**
   * Extract data variables from credit decision response
   * Handles DebtSettlementEstimate for DS scenarios (2, 4, 8, 10)
   * Handles EvenOffers for Loan scenarios (1, 2, 8)
   */
  private extractDataVariables(response: CreditDecisionResponse): DataVariables | null {
    const dataVars: DataVariables = {}

    // Check if this scenario includes Debt Settlement data (DS Only: 4, 10; DS+Loan: 2, 8)
    const hasDsData = response.scenario === 2 || response.scenario === 4 || response.scenario === 8 || response.scenario === 10

    // Handle both PascalCase and camelCase for DebtSettlementEstimate
    const debtSettlement = response.DebtSettlementEstimate || (response as any).debtSettlementEstimate
    if (hasDsData && debtSettlement) {
      const estimate = debtSettlement

      // Map DrPreQualDebtAmount to dr_prequal_debt with comma formatting
      if (estimate.DrPreQualDebtAmount !== undefined && estimate.DrPreQualDebtAmount !== null) {
        dataVars.dr_prequal_debt = this.formatNumberWithCommas(estimate.DrPreQualDebtAmount)
      }

      // Map EstimatedPayment to dr_program_quote
      if (estimate.EstimatedPayment !== undefined && estimate.EstimatedPayment !== null) {
        dataVars.dr_program_quote = estimate.EstimatedPayment.toFixed(2)
      }

      // Hardcode DR Est Program Length to 42
      dataVars.dr_est_program_length = '42'

      if (this.debugMode) {
        console.log('💉 Extracted data variables from DebtSettlementEstimate:', dataVars)
      }
    }

    // Handle both PascalCase and camelCase for EvenOffers
    const evenOffers = response.EvenOffers || (response as any).evenOffers

    // Handle both camelCase and PascalCase for loanOffers
    const loanOffers = evenOffers?.loanOffers || evenOffers?.LoanOffers

    // Use loan offer data whenever the API returns it (covers all scenarios: 1, 2, 6, 8, etc.)
    if (evenOffers && loanOffers && loanOffers.length > 0) {
      const firstLoanOffer = loanOffers[0]

      // Extract offer_logo from originator.images[0].url
      if (firstLoanOffer.originator?.images && firstLoanOffer.originator.images.length > 0) {
        dataVars.offer_logo = firstLoanOffer.originator.images[0].url
        if (this.debugMode) {
          console.log('🖼️ Extracted offer_logo URL:', dataVars.offer_logo)
        }
      } else {
        if (this.debugMode) {
          console.warn('⚠️ No images found in originator data:', firstLoanOffer.originator)
        }
      }

      // Extract offer_lender from originator.name
      if (firstLoanOffer.originator?.name) {
        dataVars.offer_lender = firstLoanOffer.originator.name
      }

      // Extract offer_prequal_amount from maxAmount (with comma formatting)
      if (firstLoanOffer.maxAmount !== undefined && firstLoanOffer.maxAmount !== null) {
        dataVars.offer_prequal_amount = this.formatNumberWithCommas(firstLoanOffer.maxAmount)
      }

      // Extract offer_prequal_payment from monthlyPayment (with decimal formatting)
      if (firstLoanOffer.monthlyPayment !== undefined && firstLoanOffer.monthlyPayment !== null) {
        dataVars.offer_prequal_payment = firstLoanOffer.monthlyPayment.toFixed(2)
      }

      // Extract lender_apr from maxApr
      if (firstLoanOffer.maxApr) {
        dataVars.lender_apr = firstLoanOffer.maxApr
      }

      // Extract offer_url from url
      if (firstLoanOffer.url) {
        dataVars.offer_url = firstLoanOffer.url
      }

      if (this.debugMode) {
        console.log('💉 Extracted data variables from EvenOffers:', dataVars)
      }
    }

    // Also include any data variables from the API response
    if (response.dataVariables) {
      Object.assign(dataVars, response.dataVariables)
    }

    // Return null if no data variables
    return Object.keys(dataVars).length > 0 ? dataVars : null
  }

  /**
   * Inject data variables into DOM elements with data-variable attributes
   */
  private injectDataVariables(dataVariables: DataVariables): void {
    if (this.debugMode) {
      console.log('💉 Injecting data variables into components:', dataVariables)
    }

    let injectedCount = 0

    // Iterate through each data variable
    for (const [key, value] of Object.entries(dataVariables)) {
      if (value === undefined) continue

      // Find all elements with matching data-variable attribute
      const selector = `[data-variable="${key}"]`
      const elements = document.querySelectorAll(selector)

      if (elements.length === 0) {
        if (this.debugMode) {
          console.log(`ℹ️ No elements found for data-variable="${key}"`)
        }
        continue
      }

      // Inject value into each matching element
      elements.forEach((element: Element) => {
        const htmlElement = element as HTMLElement

        // Special handling for offer_logo (image URLs)
        if (key === 'offer_logo') {
          if (htmlElement.tagName === 'IMG') {
            (htmlElement as HTMLImageElement).src = String(value)
            htmlElement.setAttribute('alt', 'Lender Logo')
          } else {
            // Support div/background image for logo
            htmlElement.style.backgroundImage = `url('${String(value)}')`
            htmlElement.style.backgroundSize = 'contain'
            htmlElement.style.backgroundRepeat = 'no-repeat'
            htmlElement.style.backgroundPosition = 'center'
          }
        }
        // Special handling for offer_url (button/link URLs)
        else if (key === 'offer_url' && (htmlElement.tagName === 'A' || htmlElement.tagName === 'BUTTON')) {
          if (htmlElement.tagName === 'A') {
            (htmlElement as HTMLAnchorElement).href = String(value)
          } else if (htmlElement.tagName === 'BUTTON') {
            // For buttons, set onclick to navigate
            htmlElement.setAttribute('onclick', `window.location.href='${String(value)}'`)
          }
        }
        else {
          // Set text content for non-special elements
          htmlElement.textContent = String(value)
        }

        // Also set as data attribute for reference
        htmlElement.setAttribute('data-value', String(value))

        injectedCount++
      })

      if (this.debugMode) {
        console.log(`💉 Injected "${key}" = "${value}" into ${elements.length} element(s)`)
      }
    }

    if (this.debugMode) {
      console.log(`✅ Data variable injection complete: ${injectedCount} total injections`)
    }
  }

  /**
   * Render multiple loan offers by cloning the first offer card
   */
  private renderMultipleOffers(response: CreditDecisionResponse): void {
    try {
      // Extract loan offers from response
      const evenOffers = response.EvenOffers || (response as any).evenOffers
      const loanOffers = evenOffers?.loanOffers || evenOffers?.LoanOffers
      
      if (!evenOffers || !loanOffers || loanOffers.length <= 1) {
        // No multiple offers to render
        return
      }

      if (this.debugMode) {
        console.log(`🔄 Rendering ${loanOffers.length} loan offers (cloning for offers 2+)`)
      }

      // Find the first offer wrapper — a complete offer unit (offer row + CTA button)
      // Prefer data-element attribute (set in Devlink JSX) but fall back to class selector
      // for Webflow pages where data-element hasn't been added in the designer yet
      const offerWrapper = (
        document.querySelector('[data-element="offer-wrapper"]') ||
        document.querySelector('.offer-wrapper')
      )

      if (!offerWrapper || !offerWrapper.parentElement) {
        if (this.debugMode) {
          console.warn('⚠️ Could not find offer wrapper for cloning (tried [data-element="offer-wrapper"] and .offer-wrapper)')
        }
        return
      }

      // Clone the ENTIRE offer wrapper for each additional offer (offer row + CTA included)
      // Insert after the previous offer in order, so offers appear as 1, 2, 3...
      let insertAfter: Element = offerWrapper
      for (let i = 1; i < loanOffers.length; i++) {
        const offer = loanOffers[i]

        // Deep clone the complete offer wrapper (includes logo, copy, CTA link)
        const clonedWrapper = offerWrapper.cloneNode(true) as HTMLElement

        // Inject offer-specific data into the clone
        this.injectOfferDataIntoCard(clonedWrapper, offer)

        // Insert immediately after the previous offer wrapper
        insertAfter.insertAdjacentElement('afterend', clonedWrapper)
        insertAfter = clonedWrapper

        if (this.debugMode) {
          console.log(`✅ Cloned and populated offer ${i + 1}:`, {
            lender: offer.originator?.name,
            amount: offer.maxAmount,
            payment: offer.monthlyPayment,
            apr: offer.maxApr
          })
        }
      }

      if (this.debugMode) {
        console.log(`🎉 Successfully rendered ${loanOffers.length} total loan offers`)
      }
    } catch (error) {
      console.error('❌ Error rendering multiple offers:', error)
      // Don't throw - gracefully degrade to showing just the first offer
    }
  }

  /**
   * Inject loan offer data directly into a cloned offer card
   */
  private injectOfferDataIntoCard(card: HTMLElement, offer: any): void {
    // Extract offer_logo from originator.images[0].url
    if (offer.originator?.images && offer.originator.images.length > 0) {
      const logoElement = card.querySelector('[data-variable="offer_logo"]') as HTMLElement
      if (logoElement) {
        const imageUrl = offer.originator.images[0].url
        if (logoElement.tagName === 'IMG') {
          (logoElement as HTMLImageElement).src = imageUrl
          logoElement.setAttribute('alt', offer.originator.name || 'Lender Logo')
        } else {
          // Support div/background image for logo
          logoElement.style.backgroundImage = `url('${imageUrl}')`
          logoElement.style.backgroundSize = 'contain'
          logoElement.style.backgroundRepeat = 'no-repeat'
          logoElement.style.backgroundPosition = 'center'
        }
      }
    }

    // Extract offer_lender from originator.name
    if (offer.originator?.name) {
      const lenderElement = card.querySelector('[data-variable="offer_lender"]')
      if (lenderElement) {
        lenderElement.textContent = offer.originator.name
      }
    }

    // Extract offer_prequal_amount from maxAmount (with comma formatting)
    if (offer.maxAmount !== undefined && offer.maxAmount !== null) {
      const amountElement = card.querySelector('[data-variable="offer_prequal_amount"]')
      if (amountElement) {
        amountElement.textContent = this.formatNumberWithCommas(offer.maxAmount)
      }
    }

    // Extract offer_prequal_payment from monthlyPayment (with decimal formatting)
    if (offer.monthlyPayment !== undefined && offer.monthlyPayment !== null) {
      const paymentElement = card.querySelector('[data-variable="offer_prequal_payment"]')
      if (paymentElement) {
        paymentElement.textContent = offer.monthlyPayment.toFixed(2)
      }
    }

    // Extract lender_apr from maxApr
    if (offer.maxApr) {
      const aprElement = card.querySelector('[data-variable="lender_apr"]')
      if (aprElement) {
        aprElement.textContent = offer.maxApr
      }
    }

    // Extract offer_url from url
    if (offer.url) {
      const urlElement = card.querySelector('[data-variable="offer_url"]') as HTMLAnchorElement
      if (urlElement) {
        urlElement.href = offer.url
      }
    }
  }

  /**
   * Hash a string using SHA-256 for Google Ads Enhanced Conversions
   */
  private async hashSHA256(value: string | undefined): Promise<string | undefined> {
    if (!value || value.trim().length === 0) {
      return undefined
    }

    try {
      // Normalize: lowercase and trim whitespace
      const normalized = value.toLowerCase().trim()

      // Convert string to ArrayBuffer
      const encoder = new TextEncoder()
      const data = encoder.encode(normalized)

      // Hash using SubtleCrypto (browser native)
      const hashBuffer = await crypto.subtle.digest('SHA-256', data)

      // Convert ArrayBuffer to hex string
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

      return hashHex
    } catch (error) {
      if (this.debugMode) {
        console.warn('⚠️ Failed to hash value:', error)
      }
      return undefined
    }
  }

  /**
   * Format phone number for hashing (strip all non-digits, add +1)
   * Example: "(555) 555-5555" -> "+15555555555"
   * Example: "555-555-5555" -> "+15555555555"
   * Google requires E.164 format: +[country code][number]
   */
  private formatPhoneForHashing(phone: string | undefined): string | undefined {
    if (!phone) return undefined
    // Remove ALL non-digit characters and add +1 prefix per Google requirements
    const digitsOnly = phone.replace(/\D/g, '')
    if (!digitsOnly) return undefined
    return `+1${digitsOnly}`
  }

  /**
   * Get current time formatted as 'yyyy-MM-dd HH:mm:ssZZ' for Google Ads
   * Example: "2025-12-04 14:30:00-05:00"
   */
  private getFormattedConversionTime(): string {
    const now = new Date();
    
    const pad = (n: number) => n.toString().padStart(2, '0');
    
    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1);
    const day = pad(now.getDate());
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    const seconds = pad(now.getSeconds());
    
    // Get timezone offset in minutes (e.g., 300 for EST)
    // Note: getTimezoneOffset() returns positive for behind UTC, negative for ahead
    const offsetMinutes = now.getTimezoneOffset();
    const offsetSign = offsetMinutes > 0 ? '-' : '+';
    const absOffset = Math.abs(offsetMinutes);
    const offsetHours = pad(Math.floor(absOffset / 60));
    const offsetMins = pad(absOffset % 60);
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}:${offsetMins}`;
  }

  /**
   * Fire GTM conversion event via dataLayer with enhanced conversions data
   */
  private async fireGTMConversionEvent(
    sessionId: string,
    scenarioCode: number,
    formData?: FormCompletionData['formData']
  ): Promise<void> {
    try {
      // Ensure dataLayer exists
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || []

        // Prepare enhanced conversions data if form data is available
        const enhancedConversionData: Record<string, any> = {
          // Add conversion time in required format
          conversion_time: this.getFormattedConversionTime()
        }

        if (formData) {
          // Hash PII fields for Google Ads Enhanced Conversions
          // According to Google's spec, these should be SHA-256 hashed and normalized
          const [hashedEmail, hashedPhone, hashedFirstName, hashedLastName, hashedStreet] = await Promise.all([
            this.hashSHA256(formData.email),
            this.hashSHA256(this.formatPhoneForHashing(formData.phone)),
            this.hashSHA256(formData.first_name),
            this.hashSHA256(formData.last_name),
            this.hashSHA256(formData.address_street)
          ])

          // Build enhanced conversion data object with hashed values
          // Google's format: field names WITHOUT "sha256_" prefix - hashing is implied
          if (hashedEmail) enhancedConversionData.email = hashedEmail
          if (hashedPhone) enhancedConversionData.phone_number = hashedPhone
          if (hashedFirstName) enhancedConversionData.first_name = hashedFirstName
          if (hashedLastName) enhancedConversionData.last_name = hashedLastName

          // Address fields (only include street - Google's enhanced conversion format)
          if (hashedStreet) {
            enhancedConversionData.address = {
              street: hashedStreet
            }
          }
        }

        // Push conversion event to dataLayer with enhanced conversions
        // Strip "sess_" prefix from session ID for cleaner tracking
        const cleanSessionId = sessionId.replace(/^sess_/, '')

        const eventData = {
          event: 'conversion',
          session_id: cleanSessionId,
          enhanced_conversion_data: Object.keys(enhancedConversionData).length > 0 ? enhancedConversionData : undefined
        }

        window.dataLayer.push(eventData)

        // Log to Supabase (sanitize enhanced_conversion_data)
        const logData: Record<string, unknown> = {
          event: 'conversion',
          session_id: cleanSessionId,
          scenario_code: scenarioCode
        }

        // Don't log PII from enhanced_conversion_data
        if (Object.keys(enhancedConversionData).length > 0) {
          logData.enhanced_conversion_data = {
            fields_present: Object.keys(enhancedConversionData)
          }
        }

        logger.info(LogCategory.ANALYTICS, 'GTM: conversion', logData)

        if (this.debugMode) {
          console.log('📊 GTM Conversion Event Pushed to dataLayer:', {
            event: 'conversion',
            session_id: cleanSessionId,
            scenario_code: scenarioCode,
            has_enhanced_data: Object.keys(enhancedConversionData).length > 0,
            enhanced_fields: Object.keys(enhancedConversionData)
          })
        }
      }
    } catch (error) {
      console.error('❌ Failed to push GTM conversion event:', error)
    }
  }

  /**
   * Fire prime_qualified event via dataLayer for scenarios 2 and 4
   * Includes enhanced conversions data like the main conversion event
   */
  private async firePrimeQualifiedEvent(
    sessionId: string,
    scenarioCode: number,
    formData?: FormCompletionData['formData']
  ): Promise<void> {
    try {
      // Ensure dataLayer exists
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || []

        // Prepare enhanced conversions data if form data is available
        const enhancedConversionData: Record<string, any> = {
          // Add conversion time in required format
          conversion_time: this.getFormattedConversionTime()
        }

        if (formData) {
          // Hash PII fields for Google Ads Enhanced Conversions
          const [hashedEmail, hashedPhone, hashedFirstName, hashedLastName, hashedStreet] = await Promise.all([
            this.hashSHA256(formData.email),
            this.hashSHA256(this.formatPhoneForHashing(formData.phone)),
            this.hashSHA256(formData.first_name),
            this.hashSHA256(formData.last_name),
            this.hashSHA256(formData.address_street)
          ])

          // Build enhanced conversion data object with hashed values
          if (hashedEmail) enhancedConversionData.email = hashedEmail
          if (hashedPhone) enhancedConversionData.phone_number = hashedPhone
          if (hashedFirstName) enhancedConversionData.first_name = hashedFirstName
          if (hashedLastName) enhancedConversionData.last_name = hashedLastName

          // Address fields (only include street)
          if (hashedStreet) {
            enhancedConversionData.address = {
              street: hashedStreet
            }
          }
        }

        // Push prime_qualified event to dataLayer
        const cleanSessionId = sessionId.replace(/^sess_/, '')

        const eventData = {
          event: 'prime_qualified',
          session_id: cleanSessionId,
          scenario_code: scenarioCode,
          enhanced_conversion_data: Object.keys(enhancedConversionData).length > 0 ? enhancedConversionData : undefined
        }

        window.dataLayer.push(eventData)

        // Log to Supabase (sanitize enhanced_conversion_data)
        const logData: Record<string, unknown> = {
          event: 'prime_qualified',
          session_id: cleanSessionId,
          scenario_code: scenarioCode
        }

        // Don't log PII from enhanced_conversion_data
        if (Object.keys(enhancedConversionData).length > 0) {
          logData.enhanced_conversion_data = {
            fields_present: Object.keys(enhancedConversionData)
          }
        }

        logger.info(LogCategory.ANALYTICS, 'GTM: prime_qualified', logData)

        if (this.debugMode) {
          console.log('🎯 Prime Qualified Event Pushed to dataLayer:', {
            event: 'prime_qualified',
            session_id: cleanSessionId,
            scenario_code: scenarioCode,
            has_enhanced_data: Object.keys(enhancedConversionData).length > 0,
            enhanced_fields: Object.keys(enhancedConversionData)
          })
        }
      }
    } catch (error) {
      console.error('❌ Failed to push prime_qualified event:', error)
    }
  }

  /**
   * Fire scenario-specific conversion event via dataLayer
   * Maps scenario codes to conversion names (O1, O2, O4, O5, O7, O8, O10)
   * Includes enhanced conversions data for Google Ads
   */
  private async fireScenarioConversionEvent(
    sessionId: string,
    scenarioCode: number,
    formData?: FormCompletionData['formData']
  ): Promise<void> {
    try {
      // Map scenario codes to conversion names (brand-specific)
      const accsConversionMap: Record<number, string> = {
        1: 'O1',   // Loan Only
        2: 'O2',   // DS + Loan (Prime)
        4: 'O4',   // DS Only (Prime)
        5: 'O5',   // Unqualified
        7: 'O7',   // Unqualified (Alternative)
        8: 'O8',   // DS + Loan Combo
        10: 'O10'  // DS Only
        // Note: Scenarios 3, 6, 9 are Fallback - no conversion events
      }

      const creditDirectConversionMap: Record<number, string> = {
        1: 'O1',   // Partner Only
        2: 'O2',   // DS + Loan (Prime)
        3: 'O3',   // App Process Error
        4: 'O4',   // DS Only (Prime)
        5: 'O5',   // Unqualified
        6: 'O6',   // Loan Only (Direct)
        7: 'O7',   // Unqualified (Alternative)
        8: 'O8',   // DS + Loan Combo
        9: 'O9',   // App Process Error (Alternative)
        10: 'O10'  // DS Only
      }

      // Select the appropriate conversion map based on brand
      const scenarioConversionMap = this.brand === 'creditdirect'
        ? creditDirectConversionMap
        : accsConversionMap

      // Check if this scenario has a conversion event configured
      const conversionName = scenarioConversionMap[scenarioCode]
      if (!conversionName) {
        if (this.debugMode) {
          console.log(`ℹ️ No conversion event configured for scenario ${scenarioCode}`)
        }
        return
      }

      // Ensure dataLayer exists
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || []

        // Prepare enhanced conversions data if form data is available
        const enhancedConversionData: Record<string, any> = {
          // Add conversion time in required format
          conversion_time: this.getFormattedConversionTime()
        }

        if (formData) {
          // Hash PII fields for Google Ads Enhanced Conversions
          const [hashedEmail, hashedPhone, hashedFirstName, hashedLastName, hashedStreet] = await Promise.all([
            this.hashSHA256(formData.email),
            this.hashSHA256(this.formatPhoneForHashing(formData.phone)),
            this.hashSHA256(formData.first_name),
            this.hashSHA256(formData.last_name),
            this.hashSHA256(formData.address_street)
          ])

          // Build enhanced conversion data object with hashed values
          if (hashedEmail) enhancedConversionData.email = hashedEmail
          if (hashedPhone) enhancedConversionData.phone_number = hashedPhone
          if (hashedFirstName) enhancedConversionData.first_name = hashedFirstName
          if (hashedLastName) enhancedConversionData.last_name = hashedLastName

          // Address fields (only include street)
          if (hashedStreet) {
            enhancedConversionData.address = {
              street: hashedStreet
            }
          }
        }

        // Push scenario conversion event to dataLayer
        const cleanSessionId = sessionId.replace(/^sess_/, '')

        const eventData = {
          event: conversionName,
          session_id: cleanSessionId,
          scenario_code: scenarioCode,
          enhanced_conversion_data: Object.keys(enhancedConversionData).length > 0 ? enhancedConversionData : undefined
        }

        window.dataLayer.push(eventData)

        // Log to Supabase (sanitize enhanced_conversion_data)
        const logData: Record<string, unknown> = {
          event: conversionName,
          session_id: cleanSessionId,
          scenario_code: scenarioCode
        }

        // Don't log PII from enhanced_conversion_data
        if (Object.keys(enhancedConversionData).length > 0) {
          logData.enhanced_conversion_data = {
            fields_present: Object.keys(enhancedConversionData)
          }
        }

        logger.info(LogCategory.ANALYTICS, `GTM: ${conversionName}`, logData)

        if (this.debugMode) {
          console.log(`🎯 Scenario Conversion Event "${conversionName}" Pushed to dataLayer:`, {
            event: conversionName,
            session_id: cleanSessionId,
            scenario_code: scenarioCode,
            has_enhanced_data: Object.keys(enhancedConversionData).length > 0,
            enhanced_fields: Object.keys(enhancedConversionData)
          })
        }
      }
    } catch (error) {
      console.error('❌ Failed to push scenario conversion event:', error)
    }
  }

  /**
   * Send lead attributes to LogRocket for session recording enrichment
   */
  private sendSessionRecordingAttributes(
    sessionId: string,
    scenarioCode: number,
    formData?: FormCompletionData['formData'],
    creditDecision?: CreditDecisionResponse
  ): void {
    try {
      // Check if LogRocket is available
      if (typeof window === 'undefined' || !(window as any).LogRocket) {
        if (this.debugMode) {
          console.log('ℹ️ LogRocket not available, skipping attribute tracking')
        }
        return
      }

      // Clean session ID for LogRocket
      const cleanSessionId = sessionId.replace(/^sess_/, '')

      // Build attributes object (avoid sending raw PII)
      const attributes: Record<string, any> = {
        'Session ID': cleanSessionId,
        'Brand': this.brand,
        'Scenario': scenarioCode,
        'Form Submitted': new Date().toISOString(),
      }

      // Add form data attributes
      // Note: Using type assertion since formData may contain additional fields beyond the type definition
      if (formData) {
        const extendedFormData = formData as any

        // Name and email — LogRocket first-class fields, shows in session list and enables search by customer
        if (extendedFormData.first_name && extendedFormData.last_name) {
          attributes['name'] = `${extendedFormData.first_name} ${extendedFormData.last_name}`
        } else if (extendedFormData.first_name) {
          attributes['name'] = extendedFormData.first_name
        }
        if (extendedFormData.email) {
          attributes['email'] = extendedFormData.email
        }

        // Amounts (no other PII)
        if (extendedFormData.loan_amount) {
          attributes['Loan Amount'] = parseInt(extendedFormData.loan_amount)
        }
        if (extendedFormData.debt_amount) {
          attributes['Debt Amount'] = parseInt(extendedFormData.debt_amount)
        }
        if (extendedFormData.monthly_income) {
          attributes['Monthly Income'] = parseInt(extendedFormData.monthly_income)
        }
        if (extendedFormData.employment_status) {
          attributes['Employment Status'] = extendedFormData.employment_status
        }
      }

      // Add credit decision attributes
      if (creditDecision) {
        attributes['Decision Status'] = creditDecision.decision || 'Received'

        // Check for loan offers
        const evenOffers = creditDecision.EvenOffers || (creditDecision as any).evenOffers
        if (evenOffers?.loanOffers && evenOffers.loanOffers.length > 0) {
          attributes['Loan Offers Count'] = evenOffers.loanOffers.length
          attributes['First Offer Lender'] = evenOffers.loanOffers[0].originator?.name || 'Unknown'
          attributes['First Offer Amount'] = evenOffers.loanOffers[0].maxAmount || 0
        }

        // Check for debt settlement estimate
        if (creditDecision.DebtSettlementEstimate || (creditDecision as any).debtSettlementEstimate) {
          const estimate = creditDecision.DebtSettlementEstimate || (creditDecision as any).debtSettlementEstimate
          if (estimate.monthlyPayment) {
            attributes['DS Monthly Payment'] = estimate.monthlyPayment
          }
          if (estimate.totalMonthlySavings) {
            attributes['DS Monthly Savings'] = estimate.totalMonthlySavings
          }
        }
      }

      // Identify user in LogRocket
      ;(window as any).LogRocket.identify(cleanSessionId, attributes)

      // Capture session URL and persist it to the database
      ;(window as any).LogRocket.getSessionURL((sessionUrl: string) => {
        this.storeLogRocketUrl(sessionUrl, sessionId)
      })

      if (this.debugMode) {
        console.log('📊 LogRocket attributes sent:', attributes)
      }
    } catch (error) {
      console.error('❌ Failed to send LogRocket attributes:', error)
      // Don't throw - session recording tracking is non-critical
    }
  }

  /**
   * Persist the LogRocket session URL to the simple_leads record
   */
  private storeLogRocketUrl(url: string, sessionId: string): void {
    try {
      const config = (window as any).__LEAD_TRACKER_CONFIG__
      if (!config?.supabase) {
        if (this.debugMode) {
          console.warn('⚠️ Cannot store LogRocket URL: Supabase config not available')
        }
        return
      }

      fetch(`${config.supabase.url}/rest/v1/simple_leads?session_id=eq.${sessionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'apikey': config.supabase.anonKey,
          'Authorization': `Bearer ${config.supabase.anonKey}`,
          'Prefer': 'return=minimal'
        },
        credentials: 'omit', // required - Supabase uses wildcard CORS
        body: JSON.stringify({ logrocket_url: url })
      }).then(response => {
        if (this.debugMode) {
          if (response.ok) {
            console.log('✅ LogRocket URL stored:', url)
          } else {
            console.warn('⚠️ Failed to store LogRocket URL:', response.status)
          }
        }
      }).catch(error => {
        if (this.debugMode) {
          console.warn('⚠️ Error storing LogRocket URL:', error)
        }
      })
    } catch (error) {
      // Non-critical — don't propagate
    }
  }

  /**
   * Save completion state to localStorage
   * Persists for 24 hours to prevent duplicate form submissions on page refresh
   */
  private saveCompletionState(
    sessionId: string,
    scenario: number,
    creditDecision?: CreditDecisionResponse,
    dataVariables?: DataVariables | null,
    componentActions?: { show: string[]; hide: string[]; useFallback: boolean }
  ): void {
    try {
      const state: CompletionState = {
        sessionId,
        scenario,
        timestamp: Date.now(),
        expiresAt: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
        hasCompletedForm: true,
        creditDecision,
        dataVariables: dataVariables || undefined,
        componentActions,
        formData: this.lastFormData
      }

      localStorage.setItem(COMPLETION_STATE_KEY, JSON.stringify(state))

      if (this.debugMode) {
        console.log('💾 Saved completion state to localStorage:', {
          sessionId,
          scenario,
          expiresAt: new Date(state.expiresAt).toISOString(),
          hasCreditDecision: !!creditDecision,
          hasDataVariables: !!dataVariables,
          hasComponentActions: !!componentActions
        })
      }
    } catch (error) {
      console.error('❌ Failed to save completion state:', error)
    }
  }

  /**
   * Load completion state from localStorage
   * Returns null if not found, expired, or invalid
   */
  private loadCompletionState(): CompletionState | null {
    try {
      const saved = localStorage.getItem(COMPLETION_STATE_KEY)
      if (!saved) {
        return null
      }

      const state: CompletionState = JSON.parse(saved)

      // Validate structure
      if (!state.sessionId || !state.expiresAt || typeof state.scenario !== 'number') {
        if (this.debugMode) {
          console.warn('⚠️ Invalid completion state structure, clearing...')
        }
        this.clearCompletionState()
        return null
      }

      // Check if expired (24 hours)
      if (Date.now() > state.expiresAt) {
        if (this.debugMode) {
          console.log('⏰ Completion state expired, clearing...')
        }
        this.clearCompletionState()
        return null
      }

      if (this.debugMode) {
        const hoursRemaining = ((state.expiresAt - Date.now()) / (1000 * 60 * 60)).toFixed(1)
        console.log('📂 Loaded completion state from localStorage:', {
          sessionId: state.sessionId,
          scenario: state.scenario,
          hoursRemaining
        })
      }

      return state
    } catch (error) {
      console.error('❌ Failed to load completion state:', error)
      this.clearCompletionState()
      return null
    }
  }

  /**
   * Clear completion state from localStorage
   * Made public so lead tracker can clear state on failed restoration
   */
  clearCompletionState(): void {
    try {
      localStorage.removeItem(COMPLETION_STATE_KEY)
      if (this.debugMode) {
        console.log('🗑️ Cleared completion state from localStorage')
      }
    } catch (error) {
      console.error('❌ Failed to clear completion state:', error)
    }
  }

  /**
   * Check for saved completion state (public method for lead tracker)
   * Returns saved state if found and valid (< 24 hours old)
   */
  checkForSavedState(): CompletionState | null {
    return this.loadCompletionState()
  }

  /**
   * Restore saved component state from localStorage
   * Used on page load when user returns within 24 hours
   */
  async restoreSavedState(savedState: CompletionState): Promise<ComponentRenderingResult> {
    const startTime = Date.now()

    console.log('🔄 [COMPONENT RENDERER] Starting restoreSavedState...', savedState)

    try {
      // Check if we have saved credit decision data (instant restoration)
      const hasSavedData = savedState.creditDecision && savedState.componentActions
      console.log('🔍 [COMPONENT RENDERER] Has saved data?', hasSavedData)

      if (hasSavedData) {
        console.log('⚡ [COMPONENT RENDERER] Using saved credit decision data - instant restoration (no API call)')

        // Restore form data for offer link placeholder replacement
        if (savedState.formData) {
          this.lastFormData = savedState.formData
        }

        // Get component actions from saved state
        const componentActions = savedState.componentActions!
        const scenarioCode = savedState.scenario

        console.log('🔍 [COMPONENT RENDERER] Component actions:', {
          show: componentActions.show,
          hide: componentActions.hide,
          scenarioCode: scenarioCode
        })

        // Render components directly from saved state
        console.log('🔍 [COMPONENT RENDERER] Calling renderComponents...')
        const renderingSuccess = await this.renderComponents(
          componentActions.show,
          componentActions.hide,
          savedState.sessionId,
          scenarioCode
        )
        console.log('✅ [COMPONENT RENDERER] renderComponents returned:', renderingSuccess)

        // Inject saved data variables
        if (savedState.dataVariables) {
          this.injectDataVariables(savedState.dataVariables)
        }

        // Render multiple loan offers if available
        if (savedState.creditDecision) {
          this.renderMultipleOffers(savedState.creditDecision)
        }

        // Fire scenario-specific conversion event (restored state)
        // Note: formData not available on restore, but event still fires without enhanced conversions
        await this.fireScenarioConversionEvent(savedState.sessionId, scenarioCode)

        // Send lead attributes to session recording (LogRocket)
        this.sendSessionRecordingAttributes(savedState.sessionId, scenarioCode, undefined, savedState.creditDecision)

        // Enable refresh prevention
        if (renderingSuccess) {
          this.enableRefreshPrevention()
          
          // Trigger screenshot for restored state
          this.triggerScreenshot(savedState.sessionId, scenarioCode, componentActions.show)
        }

        return {
          success: renderingSuccess,
          scenarioCode,
          componentsShown: componentActions.show,
          componentsHidden: componentActions.hide,
          usedFallback: componentActions.useFallback,
          processingTime: Date.now() - startTime,
          creditDecision: savedState.creditDecision
        }
      }

      // Fallback: No saved data - call API (backward compatibility)
      if (this.debugMode) {
        console.log('⚠️ No saved data found - falling back to API call')
      }

      // Show processing component
      await this.showProcessingComponent()

      // Create form completion data with saved session ID
      const formData: FormCompletionData = {
        sessionId: savedState.sessionId,
        submissionTime: savedState.timestamp,
        stepNumber: 0,
        isFormSubmission: false,
        debugMode: this.debugMode
      }

      // Call credit decision API with saved session ID
      const pollingResult = await this.pollForCreditDecision(formData)

      if (!pollingResult.success || !pollingResult.response) {
        // API failed - clear saved state and render fallback
        console.warn('⚠️ API failed on restore, clearing saved state')
        this.clearCompletionState()
        return this.renderFallbackComponents(startTime, pollingResult, undefined, savedState.sessionId)
      }

      // Get scenario from API response (could be original or scenario 90 if already applied)
      const scenarioCode = pollingResult.response.scenario
      const componentActions = getComponentActions(scenarioCode, this.brand)

      if (this.debugMode) {
        console.log('🎯 Restoring scenario:', {
          savedScenario: savedState.scenario,
          apiScenario: scenarioCode,
          brand: this.brand,
          actions: componentActions
        })
      }

      // Render components
      const renderingSuccess = await this.renderComponents(
        componentActions.show,
        componentActions.hide,
        savedState.sessionId,
        scenarioCode
      )

      // Inject data variables from API response
      const dataVariables = this.extractDataVariables(pollingResult.response)
      if (dataVariables) {
        this.injectDataVariables(dataVariables)
      }

      // Render multiple loan offers if available
      this.renderMultipleOffers(pollingResult.response)

      // Fire scenario-specific conversion event (API fallback path)
      // Note: formData not available on restore, but event still fires without enhanced conversions
      await this.fireScenarioConversionEvent(savedState.sessionId, scenarioCode)

      // Send lead attributes to session recording (LogRocket)
      this.sendSessionRecordingAttributes(savedState.sessionId, scenarioCode, undefined, pollingResult.response)

      // Enable refresh prevention for restored state
      if (renderingSuccess) {
        this.enableRefreshPrevention()
        
        // Trigger screenshot for API-restored state
        this.triggerScreenshot(savedState.sessionId, scenarioCode, componentActions.show)
      }

      // If scenario changed (e.g., now returns scenario 90), update saved state
      if (scenarioCode !== savedState.scenario) {
        if (this.debugMode) {
          console.log('📝 Scenario changed, updating saved state')
        }
        this.saveCompletionState(
          savedState.sessionId,
          scenarioCode,
          pollingResult.response,
          dataVariables,
          componentActions
        )
      }

      return {
        success: renderingSuccess,
        scenarioCode,
        componentsShown: componentActions.show,
        componentsHidden: componentActions.hide,
        usedFallback: componentActions.useFallback,
        processingTime: Date.now() - startTime,
        creditDecision: pollingResult.response,
        pollingResult
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown restore error'

      if (this.debugMode) {
        console.error('🎯 Component restore failed:', error)
      }

      // Clear saved state on error
      this.clearCompletionState()

      // Render fallback components (savedState may not be available if error occurred early)
      return this.renderFallbackComponents(startTime, undefined, errorMessage, savedState?.sessionId)
    }
  }

  /**
   * Enable browser refresh prevention
   * Shows warning when user tries to refresh/leave page after scenario is rendered
   */
  private enableRefreshPrevention(): void {
    // Only set up once
    if (this.refreshPreventionEnabled) {
      return
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Standard message for modern browsers
      const message = 'Your credit decision results are displayed. Refreshing the page will not change your results.'

      // For older browsers
      event.returnValue = message

      // For modern browsers
      return message
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    this.refreshPreventionEnabled = true

    if (this.debugMode) {
      console.log('🛡️ Browser refresh prevention enabled')
    }
  }

  /**
   * Lock page scrolling
   * Prevents user from scrolling while processing component is visible
   */
  private lockScroll(): void {
    if (this.scrollLocked) {
      return
    }

    // Save current scroll position
    this.savedScrollPosition = window.pageYOffset || document.documentElement.scrollTop

    // Add CSS to prevent scrolling
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${this.savedScrollPosition}px`
    document.body.style.width = '100%'

    this.scrollLocked = true

    if (this.debugMode) {
      console.log('🔒 Page scroll locked at position:', this.savedScrollPosition)
    }
  }

  /**
   * Unlock page scrolling
   * Restores normal scrolling after final component is rendered
   */
  private unlockScroll(): void {
    if (!this.scrollLocked) {
      return
    }

    // Remove scroll-prevention CSS
    document.body.style.overflow = ''
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''

    // Restore scroll position
    window.scrollTo(0, this.savedScrollPosition)

    this.scrollLocked = false

    if (this.debugMode) {
      console.log('🔓 Page scroll unlocked, restored position:', this.savedScrollPosition)
    }
  }

  /**
   * HELPER: Find any visible outcome component on the page
   * Used as fallback when componentActions.show is empty
   *
   * @returns string | null - Component ID of visible outcome component, or null if none found
   */
  private findVisibleOutcomeComponent(): string | null {
    try {
      // Look for any outcome component that's visible (has display block/flex)
      const outcomeSelectors = [
        '[data-component*="outcome"]',
        '[data-component*="Outcome"]'
      ]

      for (const selector of outcomeSelectors) {
        const elements = document.querySelectorAll(selector)
        for (const element of elements) {
          const htmlElement = element as HTMLElement
          const computedStyle = window.getComputedStyle(htmlElement)
          const isVisible = computedStyle.display !== 'none' &&
                          computedStyle.visibility !== 'hidden' &&
                          computedStyle.opacity !== '0'

          if (isVisible) {
            const componentId = htmlElement.getAttribute('data-component')
            if (componentId && this.debugMode) {
              console.log('📸 [SCREENSHOT] Found visible outcome component:', componentId)
            }
            return componentId
          }
        }
      }

      if (this.debugMode) {
        console.warn('⚠️ [SCREENSHOT] No visible outcome components found')
      }
      return null
    } catch (error) {
      if (this.debugMode) {
        console.error('❌ [SCREENSHOT] Error finding visible outcome component:', error)
      }
      return null
    }
  }

  /**
   * Fix retry button links to preserve query parameters (CreditDirect error outcome)
   * Ensures that when users click "Retry Your Application", all query params are preserved
   * Also clears localStorage completion state before navigating to prevent infinite loop
   *
   * @param shownComponents - Array of component IDs that are being shown
   * @param scenarioCode - Optional scenario code for scenario-specific retry behavior
   */
  private fixRetryButtonLinks(shownComponents: string[], scenarioCode?: number): void {
    try {
      // Only fix retry buttons for CreditDirect error outcome
      if (!shownComponents.includes('CdOutcomeAppProcessError')) {
        return
      }

      // Find the error outcome component
      const errorOutcome = document.querySelector('[data-component="cd-outcome-app-process-error"]')
      if (!errorOutcome) {
        if (this.debugMode) {
          console.warn('⚠️ [RETRY FIX] CdOutcomeAppProcessError element not found')
        }
        return
      }

      // Find all links within the error outcome that go to "/" (retry buttons)
      const retryButtons = errorOutcome.querySelectorAll('a[href="/"], a[href="/#"]')

      if (retryButtons.length === 0) {
        if (this.debugMode) {
          console.warn('⚠️ [RETRY FIX] No retry buttons found in error outcome')
        }
        return
      }

      // Get current query string
      const queryString = window.location.search
      const newHref = `/${queryString}`

      // Configure retry buttons based on scenario
      if (scenarioCode === 97 || scenarioCode === 98) {
        // Credit locked scenarios - special handling with countdown + confirmation
        this.setupCreditLockedRetryFlow(errorOutcome as HTMLElement, retryButtons, newHref, scenarioCode)
      } else {
        // Default behavior - simple click handler that clears state
        this.setupDefaultRetryButtons(retryButtons, newHref)
      }

      console.log(`✅ [RETRY FIX] Fixed ${retryButtons.length} retry button(s) for scenario ${scenarioCode || 'default'}`)

    } catch (error) {
      // Fail-safe: log error but don't break rendering
      if (this.debugMode) {
        console.error('❌ [RETRY FIX] Failed to fix retry button links:', error)
      }
    }
  }

  /**
   * Replace 'sid_here' placeholder in outcome component links with actual session ID
   * Used for CMS-configured affiliate/partner links that need dynamic session tracking
   */
  private replaceSessionIdPlaceholders(shownComponents: string[], sessionId?: string): void {
    try {
      if (!sessionId) {
        if (this.debugMode) {
          console.warn('⚠️ [SID REPLACE] No session ID provided, skipping placeholder replacement')
        }
        return
      }

      // Clean session ID - remove 'sess_' prefix if present
      const cleanSessionId = sessionId.replace(/^sess_/, '')

      let totalReplacements = 0

      // Process each shown component
      for (const componentId of shownComponents) {
        const component = COMPONENT_CONFIG.components[componentId]
        if (!component) continue

        // Find the component element(s)
        const elements = document.querySelectorAll(component.selector)

        elements.forEach((element) => {
          // Find all links within this component
          const links = element.querySelectorAll('a[href*="sid_here"]')

          links.forEach((link) => {
            const anchor = link as HTMLAnchorElement
            const currentHref = anchor.getAttribute('href') || ''
            const newHref = currentHref.replace(/sid_here/gi, cleanSessionId)

            anchor.setAttribute('href', newHref)
            totalReplacements++

            if (this.debugMode) {
              console.log('🔗 [SID REPLACE] Updated link:', {
                component: componentId,
                oldHref: currentHref,
                newHref
              })
            }
          })
        })
      }

      if (totalReplacements > 0) {
        console.log(`✅ [SID REPLACE] Replaced session ID placeholder in ${totalReplacements} link(s)`)
      }

    } catch (error) {
      // Fail-safe: log error but don't break rendering
      if (this.debugMode) {
        console.error('❌ [SID REPLACE] Failed to replace session ID placeholders:', error)
      }
    }
  }

  /**
   * Replace placeholders in offer links (e.g. 5k-funds URLs) with actual form data values
   * Handles URLs like: https://offer.5k-funds.com/?aid=505218&subid={SUBID_HERE}&x_clickid={CLICKID_HERE}&hpostal={ZIPCODE}&fname={FIRST}&lname={LAST}&email={EMAIL}&phhm={PHONE}
   */
  private replaceOfferLinkPlaceholders(shownComponents: string[], sessionId?: string): void {
    try {
      const formData = this.lastFormData
      if (!formData) {
        if (this.debugMode) {
          console.warn('⚠️ [OFFER LINK] No form data available, skipping offer link placeholder replacement')
        }
        return
      }

      const cleanSessionId = sessionId ? sessionId.replace(/^sess_/, '') : ''

      // Build replacement map: placeholder → value
      const replacements: Record<string, string> = {
        '{SUBID_HERE}': cleanSessionId,
        '{CLICKID_HERE}': cleanSessionId,
        '{ZIPCODE}': formData.address_zip || '',
        '{FIRST}': formData.first_name || '',
        '{LAST}': formData.last_name || '',
        '{EMAIL}': formData.email || '',
        '{PHONE}': formData.phone || '',
      }

      let totalReplacements = 0

      for (const componentId of shownComponents) {
        const component = COMPONENT_CONFIG.components[componentId]
        if (!component) continue

        const elements = document.querySelectorAll(component.selector)

        elements.forEach((element) => {
          // Find all links with 5k-funds in the href
          const links = element.querySelectorAll('a[href*="5k-funds"]')

          links.forEach((link) => {
            const anchor = link as HTMLAnchorElement
            let currentHref = anchor.getAttribute('href') || ''

            // Replace each placeholder
            for (const [placeholder, value] of Object.entries(replacements)) {
              currentHref = currentHref.replace(placeholder, encodeURIComponent(value))
            }

            anchor.setAttribute('href', currentHref)
            totalReplacements++

            if (this.debugMode) {
              console.log('🔗 [OFFER LINK] Updated offer link:', {
                component: componentId,
                newHref: currentHref
              })
            }
          })
        })
      }

      if (totalReplacements > 0) {
        console.log(`✅ [OFFER LINK] Replaced placeholders in ${totalReplacements} offer link(s)`)
      }

    } catch (error) {
      if (this.debugMode) {
        console.error('❌ [OFFER LINK] Failed to replace offer link placeholders:', error)
      }
    }
  }

  /**
   * Setup default retry buttons with localStorage clearing
   * Used for scenario 0 (technical error) and other non-credit-locked scenarios
   */
  private setupDefaultRetryButtons(retryButtons: NodeListOf<Element>, newHref: string): void {
    retryButtons.forEach((button) => {
      const link = button as HTMLAnchorElement
      const currentHref = link.getAttribute('href') || '/'

      // Update href to preserve query params
      link.setAttribute('href', newHref)

      // Add click handler to clear localStorage before navigation
      link.addEventListener('click', (e) => {
        e.preventDefault()

        if (this.debugMode) {
          console.log('🔄 [RETRY] Clearing completion state and navigating...')
        }

        // Clear completion state to prevent infinite loop
        this.clearCompletionState()

        // Navigate to home with query params
        window.location.href = newHref
      })

      if (this.debugMode) {
        console.log('✅ [RETRY FIX] Updated retry button:', {
          oldHref: currentHref,
          newHref,
          hasClickHandler: true
        })
      }
    })
  }

  /**
   * Setup credit locked retry flow with countdown timer, confirmation checkbox, and retry limit
   * Used for scenarios 97 (credit locked - Experian) and 98 (locked or no record - legacy)
   */
  private setupCreditLockedRetryFlow(
    container: HTMLElement,
    retryButtons: NodeListOf<Element>,
    newHref: string,
    scenarioCode: number
  ): void {
    // Check retry count first
    const currentRetryCount = this.getCreditLockedRetryCount()

    if (currentRetryCount >= MAX_CREDIT_LOCKED_RETRIES) {
      // User has already used their retry - hide buttons and show message
      if (this.debugMode) {
        console.log('🚫 [CREDIT LOCKED] Max retries reached, hiding retry buttons')
      }
      this.hideRetryButtonsAndShowLimit(container, retryButtons)
      return
    }

    // Inject countdown and confirmation UI
    const countdownDiv = this.injectCreditLockedCountdownUI(container)
    const checkbox = this.injectCreditLockedConfirmationCheckbox(container)

    // Disable retry buttons initially
    retryButtons.forEach((button) => {
      const link = button as HTMLAnchorElement
      link.setAttribute('href', newHref)
      link.style.pointerEvents = 'none'
      link.style.opacity = '0.5'
      link.style.cursor = 'not-allowed'
    })

    // Track countdown and checkbox state
    let countdownComplete = false
    let checkboxChecked = false

    // Start countdown timer
    this.startCreditLockedCountdown(countdownDiv, () => {
      countdownComplete = true
      this.updateRetryButtonState(retryButtons, countdownComplete, checkboxChecked)
    })

    // Add checkbox change handler
    checkbox.addEventListener('change', () => {
      checkboxChecked = checkbox.checked
      this.updateRetryButtonState(retryButtons, countdownComplete, checkboxChecked)
    })

    // Add click handler to retry buttons
    retryButtons.forEach((button) => {
      const link = button as HTMLAnchorElement

      link.addEventListener('click', (e) => {
        e.preventDefault()

        // Double-check conditions before allowing retry
        if (!countdownComplete || !checkboxChecked) {
          if (this.debugMode) {
            console.log('⚠️ [CREDIT LOCKED] Retry blocked - conditions not met', {
              countdownComplete,
              checkboxChecked
            })
          }
          return
        }

        if (this.debugMode) {
          console.log('🔄 [CREDIT LOCKED] Processing retry...')
        }

        // Increment retry count
        this.incrementCreditLockedRetryCount()

        // Clear completion state
        this.clearCompletionState()

        // Navigate to home
        window.location.href = newHref
      })
    })
  }

  /**
   * Inject countdown UI for credit locked scenario
   */
  private injectCreditLockedCountdownUI(container: HTMLElement): HTMLDivElement {
    const countdownDiv = document.createElement('div')
    countdownDiv.className = 'credit-unlock-countdown'
    countdownDiv.style.cssText = `
      margin: 20px 0;
      padding: 15px;
      background: #FEF3C7;
      border: 1px solid #F59E0B;
      border-radius: 8px;
      text-align: center;
    `
    countdownDiv.innerHTML = `
      <p style="margin: 0 0 10px; font-weight: 500; color: #92400E;">
        Please unlock your credit in the Experian app, then retry.
      </p>
      <p id="countdown-text" style="margin: 0; font-size: 18px; font-weight: bold; color: #B45309;">
        You can retry in ${CREDIT_LOCKED_COUNTDOWN_SECONDS} seconds
      </p>
    `

    // Find the first retry button and insert before it
    const firstRetryButton = container.querySelector('a[href="/"], a[href="/#"]')
    if (firstRetryButton && firstRetryButton.parentNode) {
      firstRetryButton.parentNode.insertBefore(countdownDiv, firstRetryButton)
    } else {
      container.appendChild(countdownDiv)
    }

    return countdownDiv
  }

  /**
   * Inject confirmation checkbox for credit locked scenario
   */
  private injectCreditLockedConfirmationCheckbox(container: HTMLElement): HTMLInputElement {
    const checkboxWrapper = document.createElement('div')
    checkboxWrapper.className = 'credit-unlock-confirmation'
    checkboxWrapper.style.cssText = `
      margin: 15px 0;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
    `
    checkboxWrapper.innerHTML = `
      <input type="checkbox" id="credit-unlocked-confirm" style="width: 20px; height: 20px; cursor: pointer;">
      <label for="credit-unlocked-confirm" style="cursor: pointer; font-weight: 500; color: #374151;">
        I have unlocked my credit in the Experian app
      </label>
    `

    // Find the first retry button and insert before it
    const firstRetryButton = container.querySelector('a[href="/"], a[href="/#"]')
    if (firstRetryButton && firstRetryButton.parentNode) {
      firstRetryButton.parentNode.insertBefore(checkboxWrapper, firstRetryButton)
    } else {
      container.appendChild(checkboxWrapper)
    }

    return checkboxWrapper.querySelector('input') as HTMLInputElement
  }

  /**
   * Start countdown timer for credit locked retry
   */
  private startCreditLockedCountdown(countdownDiv: HTMLDivElement, onComplete: () => void): void {
    let secondsRemaining = CREDIT_LOCKED_COUNTDOWN_SECONDS
    const countdownText = countdownDiv.querySelector('#countdown-text') as HTMLElement | null

    const updateCountdown = () => {
      if (countdownText) {
        if (secondsRemaining > 0) {
          countdownText.textContent = `You can retry in ${secondsRemaining} seconds`
        } else {
          countdownText.textContent = '✓ You may now retry your application'
          countdownText.style.color = '#059669'
        }
      }
    }

    const interval = setInterval(() => {
      secondsRemaining--
      updateCountdown()

      if (secondsRemaining <= 0) {
        clearInterval(interval)
        onComplete()
      }
    }, 1000)

    // Initial update
    updateCountdown()
  }

  /**
   * Update retry button enabled state based on countdown and checkbox
   */
  private updateRetryButtonState(
    retryButtons: NodeListOf<Element>,
    countdownComplete: boolean,
    checkboxChecked: boolean
  ): void {
    const canRetry = countdownComplete && checkboxChecked

    retryButtons.forEach((button) => {
      const link = button as HTMLAnchorElement
      if (canRetry) {
        link.style.pointerEvents = 'auto'
        link.style.opacity = '1'
        link.style.cursor = 'pointer'
      } else {
        link.style.pointerEvents = 'none'
        link.style.opacity = '0.5'
        link.style.cursor = 'not-allowed'
      }
    })

    if (this.debugMode) {
      console.log('🔄 [CREDIT LOCKED] Retry button state updated:', {
        countdownComplete,
        checkboxChecked,
        canRetry
      })
    }
  }

  /**
   * Hide retry buttons and show max retry limit message
   */
  private hideRetryButtonsAndShowLimit(container: HTMLElement, retryButtons: NodeListOf<Element>): void {
    // Add message
    const limitMessage = document.createElement('div')
    limitMessage.className = 'credit-retry-limit-message'
    limitMessage.style.cssText = `
      margin: 20px 0;
      padding: 15px;
      background: #FEE2E2;
      border: 1px solid #EF4444;
      border-radius: 8px;
      text-align: center;
    `
    limitMessage.innerHTML = `
      <p style="margin: 0; font-weight: 500; color: #991B1B;">
        You have already attempted to retry your application, but your credit is still locked or frozen.
      </p>
      <p style="margin: 10px 0 0; color: #7F1D1D;">
        Please contact a Credit Direct specialist for assistance at (877) 530-3803.
      </p>
    `

    // Replace the first retry button with the message, then hide all buttons
    const firstButton = retryButtons[0]
    if (firstButton && firstButton.parentNode) {
      firstButton.parentNode.insertBefore(limitMessage, firstButton)
    }

    // Hide all retry buttons
    retryButtons.forEach((button) => {
      (button as HTMLElement).style.display = 'none'
    })
  }

  /**
   * Get current credit locked retry count from localStorage
   */
  private getCreditLockedRetryCount(): number {
    try {
      const count = localStorage.getItem(CREDIT_LOCKED_RETRY_COUNT_KEY)
      return count ? parseInt(count, 10) : 0
    } catch {
      return 0
    }
  }

  /**
   * Increment credit locked retry count in localStorage
   */
  private incrementCreditLockedRetryCount(): void {
    try {
      const currentCount = this.getCreditLockedRetryCount()
      localStorage.setItem(CREDIT_LOCKED_RETRY_COUNT_KEY, String(currentCount + 1))

      if (this.debugMode) {
        console.log('📝 [CREDIT LOCKED] Retry count incremented:', currentCount + 1)
      }
    } catch (error) {
      if (this.debugMode) {
        console.error('❌ [CREDIT LOCKED] Failed to increment retry count:', error)
      }
    }
  }

  /**
   * FAIL-SAFE: Capture screenshot of rendered outcome component
   * This method is designed to never throw errors or break existing functionality
   *
   * @param componentId - The component ID to screenshot
   * @param sessionId - Session ID for file naming
   * @param scenarioCode - Scenario code for file naming
   * @returns Promise<string | null> - Screenshot URL or null if failed
   */
  private async captureOutcomeScreenshot(
    componentId: string,
    sessionId: string,
    scenarioCode: number
  ): Promise<string | null> {
    try {
      // Get the component config to use the correct selector
      const component = COMPONENT_CONFIG.components[componentId]
      if (!component) {
        if (this.debugMode) {
          console.error('❌ [SCREENSHOT] Component config not found:', componentId)
        }
        return null
      }

      // Find the component element using the configured selector
      const element = document.querySelector(component.selector)
      if (!element) {
        if (this.debugMode) {
          console.warn('⚠️ [SCREENSHOT] Component element not found:', componentId)
        }
        return null
      }

      // Scroll element into view with space above to capture logo/header
      // Use smooth: false for immediate positioning
      element.scrollIntoView({
        behavior: 'auto',
        block: 'start',
        inline: 'nearest'
      })

      // Scroll up a bit more to ensure logo/header is visible (150px buffer)
      window.scrollBy({ top: -150, behavior: 'auto' })

      // Wait for ALL images in the element to load before capturing
      await this.waitForImagesToLoad(element as HTMLElement)

      // Additional delay for images to decode/paint and animations to settle
      // Use network-aware timing: slow connections get more time for images to render
      const connection = (navigator as any).connection
      const effectiveType = connection?.effectiveType || '4g'
      const paintDelay = (effectiveType === 'slow-2g' || effectiveType === '2g' || effectiveType === '3g') ? 3000 : 1500
      await new Promise(resolve => setTimeout(resolve, paintDelay))

      // Inline cross-origin SVG images as same-origin blob URLs so html2canvas
      // can draw them to canvas without CORS tainting issues.
      // html2canvas re-fetches images internally; cross-origin SVGs from CDNs
      // that lack CORS headers are rendered blank. Blob URLs are always same-origin.
      const svgBlobMap = await this.inlineSvgImagesAsBlobUrls(element as HTMLElement)

      // Detect mobile/Facebook browser to adjust quality
      const userAgent = navigator.userAgent || ''
      const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent)
      const isFacebook = /FBAN|FBAV/i.test(userAgent)
      
      // Use lower scale for mobile/Facebook to prevent memory crashes
      // Desktop: 1.5 (High Quality)
      // Mobile: 1.0 (Standard Quality)
      // Facebook IAB: 1.0 (Standard Quality - safer)
      const initialScale = (isMobile || isFacebook) ? 1.0 : 1.5

      if (this.debugMode) {
        console.log(`📸 [SCREENSHOT] Starting capture (Mobile: ${isMobile}, FB: ${isFacebook}, Scale: ${initialScale})`)
      }

      let canvas: HTMLCanvasElement

      try {
        // First attempt with standard settings
        canvas = await html2canvas(element as HTMLElement, {
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          scale: initialScale,
          logging: false,
          imageTimeout: 15000, // Increased from 10s to 15s to match waitForImagesToLoad timeout
          scrollY: -150,
          scrollX: 0,
          windowWidth: 1200,
          onclone: (clonedDoc, clonedElement) => {
            // Fix layout for screenshot (same as before)
            this.fixScreenshotLayout(clonedDoc, clonedElement)
          }
        })
      } catch (firstError) {
        // If first attempt fails (likely memory), try again with lower quality
        if (this.debugMode) {
          console.warn('⚠️ [SCREENSHOT] First attempt failed, retrying with lower quality...', firstError)
        }
        
        try {
          canvas = await html2canvas(element as HTMLElement, {
            useCORS: true,
            allowTaint: false,
            backgroundColor: '#ffffff',
            scale: 0.75, // Low quality fallback
            logging: false,
            imageTimeout: 15000, // Increased from 10s to 15s to match waitForImagesToLoad timeout
            scrollY: -150,
            scrollX: 0,
            windowWidth: 1200,
            onclone: (clonedDoc, clonedElement) => {
              this.fixScreenshotLayout(clonedDoc, clonedElement)
            }
          })
        } catch (secondError) {
          console.error('❌ [SCREENSHOT] All capture attempts failed:', secondError)
          // Restore original SVG srcs before returning
          svgBlobMap.forEach((originalSrc, img) => {
            URL.revokeObjectURL(img.src)
            img.src = originalSrc
          })
          return null
        }
      }

      // Restore original SVG image srcs and release blob URLs
      svgBlobMap.forEach((originalSrc, img) => {
        URL.revokeObjectURL(img.src)
        img.src = originalSrc
      })

      if (this.debugMode) {
        console.log('📸 [SCREENSHOT] Canvas created:', { width: canvas.width, height: canvas.height })
      }

      // Convert canvas to base64 (no blob needed for edge function)
      // Use lower quality for JPEG/PNG compression on mobile
      const quality = (isMobile || isFacebook) ? 0.7 : 0.85
      const base64Image = canvas.toDataURL('image/png', quality)

      // Upload via edge function (keeps credentials server-side)
      const screenshotUrl = await this.uploadScreenshotViaEdgeFunction(base64Image, sessionId, scenarioCode)

      if (screenshotUrl && this.debugMode) {
        console.log('✅ [SCREENSHOT] Screenshot captured and uploaded:', screenshotUrl)
      }

      return screenshotUrl

    } catch (error) {
      // FAIL-SAFE: Log error but never throw
      if (this.debugMode) {
        console.error('❌ [SCREENSHOT] Screenshot capture failed (non-critical):', error)
      }
      return null
    }
  }

  /**
   * Helper to fix layout issues in cloned document for screenshot
   */
  private fixScreenshotLayout(clonedDoc: Document, clonedElement: Element): void {
    if (clonedElement instanceof HTMLElement) {
      clonedElement.style.width = '1200px'
      clonedElement.style.maxWidth = 'none'
      clonedElement.style.overflow = 'visible'

      // Fix all child elements to prevent clipping
      const allElements = clonedElement.querySelectorAll('*')
      allElements.forEach((el) => {
        if (el instanceof HTMLElement) {
          // Prevent overflow clipping on all containers
          const computedStyle = clonedDoc.defaultView?.getComputedStyle(el)
          if (computedStyle?.overflow === 'hidden' || computedStyle?.overflowX === 'hidden') {
            el.style.overflow = 'visible'
          }
          // Ensure images display properly
          if (el.tagName === 'IMG') {
            el.style.maxWidth = '100%'
            el.style.height = 'auto'
            el.style.display = 'block'
          }
          // Fix Webflow Row/Column grid containers
          if (el.classList.contains('w-row') || el.classList.contains('w-col') ||
              el.className.includes('offer-logo') || el.className.includes('offer')) {
            el.style.overflow = 'visible'
          }
        }
      })
    }
    if (this.debugMode) {
      console.log('📸 [SCREENSHOT] Document cloned with overflow fixes applied')
    }
  }

  /**
   * Fetches all external images within an element as Blob URLs so that
   * html2canvas can render them without CORS canvas-tainting issues.
   *
   * html2canvas re-fetches image URLs internally; if the CDN/S3 bucket doesn't
   * send Access-Control-Allow-Origin headers the canvas is tainted and the image
   * renders blank.  A blob:// URL is always same-origin, so this bypass works
   * for SVGs, PNGs, JPEGs, and any other format served cross-origin.
   *
   * Returns a Map of img → originalSrc so callers can restore after capture.
   */
  private async inlineSvgImagesAsBlobUrls(element: HTMLElement): Promise<Map<HTMLImageElement, string>> {
    const restored = new Map<HTMLImageElement, string>()
    const images = Array.from(element.querySelectorAll('img')) as HTMLImageElement[]

    // Apply to all external images (http/https) — covers SVG, PNG, JPG, etc.
    const externalImages = images.filter(img => {
      const src = img.src || ''
      return src.startsWith('http://') || src.startsWith('https://')
    })

    await Promise.all(
      externalImages.map(async (img) => {
        const originalSrc = img.src
        try {
          const response = await fetch(originalSrc, { credentials: 'omit', mode: 'cors' })
          if (!response.ok) return
          const blob = await response.blob()
          const blobUrl = URL.createObjectURL(blob)
          img.src = blobUrl
          restored.set(img, originalSrc)
          if (this.debugMode) {
            console.log(`📸 [SCREENSHOT] Image inlined as blob: ${originalSrc}`)
          }
        } catch (err) {
          // If fetch fails (e.g. network or CORS), leave the image as-is
          if (this.debugMode) {
            console.warn(`📸 [SCREENSHOT] Could not inline image (will render blank): ${originalSrc}`, err)
          }
        }
      })
    )

    return restored
  }

  /**
   * Wait for all images within an element to finish loading
   * This ensures external images (like loan offer logos) are loaded before screenshot
   * Handles both regular images and SVGs properly
   */
  private async waitForImagesToLoad(element: HTMLElement): Promise<void> {
    const images = Array.from(element.querySelectorAll('img')) as HTMLImageElement[]

    if (images.length === 0) {
      if (this.debugMode) {
        console.log('📸 [SCREENSHOT] No images found in element')
      }
      return
    }

    if (this.debugMode) {
      console.log(`📸 [SCREENSHOT] Waiting for ${images.length} images to load...`)
    }

    const imagePromises = images.map((img) => {
      return new Promise<void>((resolve) => {
        // Check if image is already loaded
        // For SVGs: naturalHeight is always 0, so check naturalWidth instead
        // For regular images: check both width and height
        const isSvg = img.src.toLowerCase().includes('.svg') || img.src.includes('image/svg')
        const isLoaded = img.complete && (
          isSvg
            ? img.naturalWidth > 0  // SVGs only have width
            : (img.naturalWidth > 0 && img.naturalHeight > 0)  // Regular images have both
        )

        if (isLoaded) {
          if (this.debugMode) {
            console.log(`✅ [SCREENSHOT] Image already loaded (${isSvg ? 'SVG' : 'PNG/JPG'}): ${img.src}`)
          }
          resolve()
          return
        }

        // Set timeout for image load (15 seconds per image - increased for slow connections)
        const timeout = setTimeout(() => {
          if (this.debugMode) {
            console.warn(`⚠️ [SCREENSHOT] Image load timeout (${isSvg ? 'SVG' : 'PNG/JPG'}): ${img.src}`)
          }
          img.removeEventListener('load', onLoad)
          img.removeEventListener('error', onError)
          resolve()
        }, 15000)

        // Use addEventListener (not img.onload =) to avoid overwriting existing handlers
        // and to prevent the race condition where the load event fires between the
        // img.complete check above and the handler assignment below
        const onLoad = () => {
          clearTimeout(timeout)
          img.removeEventListener('load', onLoad)
          img.removeEventListener('error', onError)
          if (this.debugMode) {
            console.log(`✅ [SCREENSHOT] Image loaded (${isSvg ? 'SVG' : 'PNG/JPG'}): ${img.src}`)
          }
          resolve()
        }

        const onError = () => {
          clearTimeout(timeout)
          img.removeEventListener('load', onLoad)
          img.removeEventListener('error', onError)
          if (this.debugMode) {
            console.warn(`⚠️ [SCREENSHOT] Image failed to load (${isSvg ? 'SVG' : 'PNG/JPG'}): ${img.src}`)
          }
          resolve() // Resolve anyway to not block screenshot
        }

        img.addEventListener('load', onLoad)
        img.addEventListener('error', onError)

        // Double-check AFTER adding listeners — covers the race window where
        // the image finishes loading between the isLoaded check above and addEventListener
        const recheckLoaded = img.complete && (
          isSvg ? img.naturalWidth > 0 : (img.naturalWidth > 0 && img.naturalHeight > 0)
        )
        if (recheckLoaded) {
          clearTimeout(timeout)
          img.removeEventListener('load', onLoad)
          img.removeEventListener('error', onError)
          if (this.debugMode) {
            console.log(`✅ [SCREENSHOT] Image loaded on recheck (race window): ${img.src}`)
          }
          resolve()
        }
      })
    })

    await Promise.all(imagePromises)

    if (this.debugMode) {
      console.log(`✅ [SCREENSHOT] All images loaded (or timed out)`)
    }
  }

  /**
   * FAIL-SAFE: Upload screenshot blob to Supabase Storage
   * This method is designed to never throw errors or break existing functionality
   *
   * @param blob - Image blob to upload
   * @param sessionId - Session ID for file naming
   * @param scenarioCode - Scenario code for file naming
   * @returns Promise<string | null> - Public URL or null if failed
   */
  private async uploadScreenshotToStorage(
    blob: Blob,
    sessionId: string,
    scenarioCode: number
  ): Promise<string | null> {
    try {
      // Import Supabase client dynamically to avoid circular dependencies
      const { createClient } = await import('@supabase/supabase-js')

      // Get Supabase credentials from environment
      const supabaseUrl = this.envConfig.supabaseUrl || process.env.NEXT_PUBLIC_SUPABASE_URL
      const supabaseAnonKey = this.envConfig.supabaseAnonKey || process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY

      if (!supabaseUrl || !supabaseAnonKey) {
        if (this.debugMode) {
          console.warn('⚠️ [SCREENSHOT] Supabase credentials not available, skipping upload')
        }
        return null
      }

      const supabase = createClient(supabaseUrl, supabaseAnonKey)

      // Create unique filename
      const cleanSessionId = sessionId.replace(/^sess_/, '')
      const timestamp = Date.now()
      const fileName = `${cleanSessionId}_scenario${scenarioCode}_${timestamp}.png`
      const filePath = `outcome-screenshots/${fileName}`

      if (this.debugMode) {
        console.log('📤 [SCREENSHOT] Uploading to Supabase Storage...', { filePath })
      }

      // Upload to Supabase Storage
      const { data, error: uploadError } = await supabase.storage
        .from('lead-screenshots')
        .upload(filePath, blob, {
          contentType: 'image/png',
          cacheControl: '31536000', // 1 year cache
          upsert: false // Don't overwrite existing files
        })

      if (uploadError) {
        if (this.debugMode) {
          console.warn('⚠️ [SCREENSHOT] Upload failed:', uploadError.message)
        }
        return null
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('lead-screenshots')
        .getPublicUrl(filePath)

      if (this.debugMode) {
        console.log('✅ [SCREENSHOT] Upload successful:', publicUrl)
      }

      // Update lead record with screenshot URL
      await this.updateLeadWithScreenshot(supabase, sessionId, publicUrl)

      return publicUrl

    } catch (error) {
      // FAIL-SAFE: Log error but never throw
      if (this.debugMode) {
        console.error('❌ [SCREENSHOT] Upload failed (non-critical):', error)
      }
      return null
    }
  }

  /**
   * FAIL-SAFE: Update lead record with screenshot URL
   * This method is designed to never throw errors or break existing functionality
   *
   * @param supabase - Supabase client instance
   * @param sessionId - Session ID to update
   * @param screenshotUrl - Screenshot URL to store
   */
  private async updateLeadWithScreenshot(
    supabase: any,
    sessionId: string,
    screenshotUrl: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('simple_leads')
        .update({ outcome_screenshot_url: screenshotUrl })
        .eq('session_id', sessionId)

      if (error) {
        if (this.debugMode) {
          console.warn('⚠️ [SCREENSHOT] Failed to update lead record:', error.message)
        }
      } else if (this.debugMode) {
        console.log('✅ [SCREENSHOT] Lead record updated with screenshot URL')
      }
    } catch (error) {
      // FAIL-SAFE: Log error but never throw
      if (this.debugMode) {
        console.error('❌ [SCREENSHOT] Database update failed (non-critical):', error)
      }
    }
  }

  /**
   * FAIL-SAFE: Upload screenshot to Supabase via Edge Function
   * This method is designed to never throw errors or break existing functionality
   * Keeps Supabase credentials server-side for security
   *
   * @param base64Image - Base64 encoded image data
   * @param sessionId - Session ID for file naming
   * @param scenarioCode - Scenario code for file naming
   * @returns Promise<string | null> - Public URL or null if failed
   */
  private async uploadScreenshotViaEdgeFunction(
    base64Image: string,
    sessionId: string,
    scenarioCode: number
  ): Promise<string | null> {
    try {
      // Get Supabase URL for edge function endpoint
      const supabaseUrl = this.envConfig.supabaseUrl || 'https://dwyvdtpzkkwnvowphvju.supabase.co'

      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/upload-outcome-screenshot`

      // Get Supabase anon key for authorization
      const anonKey = this.envConfig.supabaseAnonKey || 'sb_publishable_t_xEURdLDU_4Tm-BiCTu7Q_xrG---4Z'

      // Call edge function with screenshot data
      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
          'apikey': anonKey
        },
        body: JSON.stringify({
          sessionId,
          scenarioCode,
          imageBlob: base64Image
        })
      })

      if (!response.ok) {
        if (this.debugMode) {
          const errorText = await response.text()
          console.error('❌ [SCREENSHOT] Edge function failed:', response.status, errorText)
        }
        return null
      }

      const result = await response.json()

      if (result.success && result.url) {
        if (this.debugMode) {
          console.log('✅ [SCREENSHOT] Upload successful:', result.url)
        }
        return result.url
      } else {
        if (this.debugMode) {
          console.error('❌ [SCREENSHOT] Edge function returned error:', result.error)
        }
        return null
      }

    } catch (error) {
      // FAIL-SAFE: Log error but never throw
      if (this.debugMode) {
        console.error('❌ [SCREENSHOT] Upload failed (non-critical):', error)
      }
      return null
    }
  }

  /**
   * Setup click tracking for buttons and links within outcome components
   * Tracks user interactions with CTAs on outcome pages (loan offers, retry, etc.)
   */
  private setupOutcomeButtonTracking(
    shownComponents: string[],
    sessionId?: string,
    scenarioCode?: number
  ): void {
    try {
      // Only proceed if we have a session ID
      if (!sessionId) {
        if (this.debugMode) {
          console.log('⏭️ [BUTTON TRACKING] Skipping - no session ID')
        }
        return
      }

      // Track buttons that we've already attached listeners to
      const trackedButtonsKey = 'prodebtco_tracked_buttons'
      const trackedButtons: Set<Element> = (window as any)[trackedButtonsKey] || new Set()
      ;(window as any)[trackedButtonsKey] = trackedButtons

      shownComponents.forEach((componentId) => {
        const component = COMPONENT_CONFIG.components[componentId]
        if (!component) return

        const elements = document.querySelectorAll(component.selector)
        elements.forEach((element) => {
          // Find all clickable elements within the outcome component
          const clickableElements = element.querySelectorAll('a, button, [role="button"], input[type="submit"], input[type="button"]')

          clickableElements.forEach((clickable) => {
            // Skip if already tracked
            if (trackedButtons.has(clickable)) return
            trackedButtons.add(clickable)

            // Determine button type from element attributes and content
            const buttonType = this.determineButtonType(clickable as HTMLElement)
            const outcomeType = component.displayName || componentId

            clickable.addEventListener('click', (event) => {
              const targetUrl = (clickable as HTMLAnchorElement).href || undefined
              const isLink = clickable.tagName === 'A' && targetUrl

              // Validate URL if it's a link
              let urlStatus = 'no_url'
              let isValidUrl = false

              if (targetUrl) {
                try {
                  const url = new URL(targetUrl)
                  isValidUrl = url.protocol === 'http:' || url.protocol === 'https:' || url.protocol === 'tel:' || url.protocol === 'mailto:'
                  urlStatus = isValidUrl ? 'valid_url' : 'invalid_protocol'
                } catch {
                  urlStatus = 'malformed_url'
                  isValidUrl = false
                }
              }

              if (this.debugMode) {
                console.log('🖱️ [BUTTON TRACKING] Outcome button clicked:', {
                  buttonType,
                  outcomeType,
                  scenarioCode,
                  targetUrl: targetUrl ? targetUrl.substring(0, 50) + '...' : 'none',
                  urlStatus,
                  isValidUrl
                })
              }

              // Track the click event with URL validation status
              trackOutcomeButtonClick(
                sessionId,
                this.brand,
                buttonType,
                outcomeType,
                scenarioCode,
                targetUrl,
                urlStatus
              )

              // For valid external links, track navigation start using sendBeacon
              // This fires just before the page unloads, confirming redirect started
              if (isLink && isValidUrl && !event.defaultPrevented) {
                const isExternalLink = targetUrl && !targetUrl.includes(window.location.hostname)
                const isSamePageAnchor = targetUrl && targetUrl.startsWith('#')

                // Only track navigation for actual page changes
                if (isExternalLink || (!isSamePageAnchor && targetUrl !== window.location.href)) {
                  // Use beforeunload to track navigation start
                  const trackNavigation = () => {
                    // Track that navigation actually started
                    logger.info(LogCategory.ANALYTICS, 'Click Tracking: navigation_started', {
                      event: 'navigation_started',
                      session_id: sessionId,
                      brand: this.brand,
                      button_type: buttonType,
                      outcome_type: outcomeType,
                      scenario_code: scenarioCode,
                      target_url: targetUrl ? targetUrl.substring(0, 200) : undefined
                    })

                    // Remove listener after firing
                    window.removeEventListener('beforeunload', trackNavigation)
                  }

                  // Attach beforeunload listener temporarily
                  window.addEventListener('beforeunload', trackNavigation)

                  // Cleanup if navigation doesn't happen within 2 seconds
                  setTimeout(() => {
                    window.removeEventListener('beforeunload', trackNavigation)
                  }, 2000)
                }
              }
            })
          })
        })
      })

      if (this.debugMode) {
        console.log(`✅ [BUTTON TRACKING] Setup complete for ${shownComponents.length} component(s)`)
      }

    } catch (error) {
      // FAIL-SAFE: Never break rendering due to tracking errors
      if (this.debugMode) {
        console.error('❌ [BUTTON TRACKING] Setup failed (non-critical):', error)
      }
    }
  }

  /**
   * Determine the type of button based on its content and attributes
   */
  private determineButtonType(element: HTMLElement): string {
    const href = (element as HTMLAnchorElement).href || ''
    const text = element.textContent?.toLowerCase() || ''
    const ariaLabel = element.getAttribute('aria-label')?.toLowerCase() || ''

    // Check for common button types
    if (text.includes('retry') || href.includes('/') && text.includes('try again')) {
      return 'retry'
    }
    if (text.includes('loan') || text.includes('offer') || text.includes('view') && text.includes('offer')) {
      return 'view_loan_offer'
    }
    if (text.includes('apply') || text.includes('get started')) {
      return 'apply_now'
    }
    if (text.includes('call') || href.startsWith('tel:')) {
      return 'call_phone'
    }
    if (text.includes('debt') || text.includes('settlement') || text.includes('relief')) {
      return 'debt_settlement'
    }
    if (text.includes('learn more') || text.includes('more info')) {
      return 'learn_more'
    }
    if (text.includes('contact') || text.includes('schedule')) {
      return 'contact'
    }

    // Fallback to generic type based on element
    if (element.tagName === 'A' && href) {
      return 'link_click'
    }
    return 'button_click'
  }

  /**
   * Apply pre-filled data to form fields
   * @param formData - Pre-filled form data
   * @param stepsToHide - Array of step numbers to hide
   */
  public applyPreFillToForm(formData: Record<string, any>, stepsToHide: number[]): void {
    try {
      console.log('📝 Applying pre-fill to form fields...', {
        fieldsCount: Object.keys(formData).length,
        stepsToHideCount: stepsToHide.length
      })

      // Apply pre-filled data to form fields
      for (const [fieldName, fieldValue] of Object.entries(formData)) {
        if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
          continue
        }

        // Find input element by name attribute
        const input = document.querySelector(`input[name="${fieldName}"], select[name="${fieldName}"], textarea[name="${fieldName}"]`) as HTMLInputElement

        if (input) {
          // Set value based on input type
          if (input.type === 'checkbox' || input.type === 'radio') {
            if (fieldValue === true || fieldValue === 'true' || fieldValue === '1') {
              input.checked = true
            }
          } else {
            input.value = String(fieldValue)
          }

          // Trigger change event for any listeners
          const event = new Event('change', { bubbles: true })
          input.dispatchEvent(event)

          console.log(`  ✅ Set ${fieldName} = ${String(fieldValue).substring(0, 50)}`)
        } else {
          console.warn(`  ⚠️ Field not found in DOM: ${fieldName}`)
        }
      }

      // Initialize custom multi-step controller instead of hiding steps
      // This replaces the external Webflow multi-step script
      this.initializeMultiStepController(stepsToHide)

      console.log('✅ Pre-fill applied successfully')

    } catch (error) {
      console.error('❌ Error applying pre-fill to form:', error)
    }
  }

  /**
   * Initialize our custom multi-step controller
   * This replaces the external Webflow multi-step script with logic that understands pre-filled steps
   * @param stepsToHide - Array of step numbers to skip (1-based)
   */
  private initializeMultiStepController(stepsToHide: number[]): void {
    try {
      console.log('🎮 Initializing custom multi-step controller...')

      const controller = new MultiStepController(true) // Enable debug mode
      const success = controller.initialize(stepsToHide)

      if (success) {
        console.log('✅ Custom multi-step controller active')
        console.log('   This replaces the external multi-step.js script')
      } else {
        console.warn('⚠️ Multi-step controller initialization failed - falling back to default behavior')
      }
    } catch (error) {
      console.error('❌ Error initializing multi-step controller:', error)
    }
  }

  /**
   * Hide completed form steps
   * @param stepsToHide - Array of step numbers to hide (1-based)
   */
  private hideFormSteps(stepsToHide: number[]): void {
    if (stepsToHide.length === 0) {
      console.log('ℹ️ No steps to hide')
      return
    }

    console.log(`🔒 Hiding ${stepsToHide.length} form steps:`, stepsToHide)

    // Get all Webflow form steps (they use data-form="step" attribute)
    const allSteps = Array.from(document.querySelectorAll('[data-form="step"]')) as HTMLElement[]

    if (allSteps.length === 0) {
      console.warn('⚠️ No form steps found with [data-form="step"] selector')
      return
    }

    console.log(`📋 Found ${allSteps.length} total form steps`)

    // Hide steps by index (stepNumber is 1-based, array is 0-based)
    for (const stepNumber of stepsToHide) {
      const stepIndex = stepNumber - 1 // Convert to 0-based index

      if (stepIndex >= 0 && stepIndex < allSteps.length) {
        const stepElement = allSteps[stepIndex]
        stepElement.style.display = 'none'
        stepElement.setAttribute('aria-hidden', 'true')
        console.log(`  ✅ Hidden step ${stepNumber} (index ${stepIndex})`)
      } else {
        console.warn(`  ⚠️ Step ${stepNumber} is out of range (total steps: ${allSteps.length})`)
      }
    }

    // Auto-advance to first non-hidden step
    this.advanceToFirstVisibleStep(allSteps, stepsToHide)

    // Update form progress/step counter if it exists
    this.updateFormProgress(stepsToHide)
  }

  /**
   * Auto-advance to the first visible step after hiding pre-filled steps
   * @param allSteps - Array of all form step elements
   * @param hiddenSteps - Array of step numbers that were hidden (1-based)
   */
  private advanceToFirstVisibleStep(allSteps: HTMLElement[], hiddenSteps: number[]): void {
    // Find first step that's NOT in the hidden list
    let firstVisibleStepIndex = -1

    for (let i = 0; i < allSteps.length; i++) {
      const stepNumber = i + 1 // Convert to 1-based
      if (!hiddenSteps.includes(stepNumber)) {
        firstVisibleStepIndex = i
        break
      }
    }

    if (firstVisibleStepIndex === -1) {
      console.warn('⚠️ No visible steps remaining - all steps are hidden')
      return
    }

    console.log(`🎯 Auto-advancing to first non-hidden step: ${firstVisibleStepIndex + 1} (index ${firstVisibleStepIndex})`)

    // Hide ALL steps first (Webflow multi-step form pattern)
    allSteps.forEach((step, index) => {
      step.style.display = 'none'
      step.setAttribute('aria-hidden', 'true')
    })

    // Show ONLY the first non-hidden step
    const firstVisibleStep = allSteps[firstVisibleStepIndex]
    firstVisibleStep.style.display = ''
    firstVisibleStep.removeAttribute('aria-hidden')

    console.log(`✅ Now showing step ${firstVisibleStepIndex + 1}`)
  }

  /**
   * Update form progress indicator after hiding steps
   * @param hiddenSteps - Array of hidden step numbers
   */
  private updateFormProgress(hiddenSteps: number[]): void {
    // This is a placeholder - actual implementation depends on Webflow form structure
    // The form may have a progress bar or step counter that needs updating

    // Example: Update progress percentage
    const progressBar = document.querySelector('.form-progress-bar') as HTMLElement
    if (progressBar) {
      // Calculate new progress based on hidden steps
      const totalSteps = 12 // Adjust based on actual form
      const remainingSteps = totalSteps - hiddenSteps.length
      const progressPercent = ((totalSteps - remainingSteps) / totalSteps) * 100

      progressBar.style.width = `${progressPercent}%`
      console.log(`📊 Updated form progress: ${progressPercent.toFixed(0)}%`)
    }

    // Example: Update step counter
    const stepCounter = document.querySelector('.form-step-counter') as HTMLElement
    if (stepCounter) {
      const currentStep = 1 // User starts at first visible step
      const totalVisibleSteps = 12 - hiddenSteps.length
      stepCounter.textContent = `Step ${currentStep} of ${totalVisibleSteps}`
      console.log(`📊 Updated step counter: ${currentStep}/${totalVisibleSteps}`)
    }
  }

  /**
   * Get current renderer status
   */
  getStatus() {
    return {
      environment: this.options.environment,
      debugMode: this.debugMode,
      apiBaseUrl: this.envConfig.apiBaseUrl,
      pollingConfig: this.envConfig.polling,
      refreshPreventionEnabled: this.refreshPreventionEnabled
    }
  }
}